import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  ChevronRight, 
  ChevronLeft, 
  Download, 
  Settings, 
  Search, 
  Plus, 
  Trash2, 
  Upload, 
  X, 
  Check, 
  Loader2,
  Home,
  Calculator,
  FileText,
  Lock,
  LogOut,
  User,
  Building,
  IndianRupee,
  Edit2,
  Presentation,
  ArrowLeft,
  FileStack,
  Monitor,
  Activity,
  Layers,
  BookOpen
} from "lucide-react";

import HeatLoadPage from "@/pages/HeatLoadCalculator";
import TechSpecBuilder from "@/pages/TechSpecBuilder";
import EnvicomDemo from "@/pages/EnvicomDemo";
import RampCalculator from "@/pages/RampCalculator";
import PresentationBuilder from "@/pages/PresentationBuilder";
import EnvivomDemo from "@/pages/EnvivomDemo";
import LevitoIotDemo from "@/pages/LevitoIotDemo";
import LevitoFsDemo from "@/pages/LevitoFsDemo";
import ReferenceDocuments from "@/pages/ReferenceDocuments";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// ==================== AUTH CALLBACK (Emergent Google OAuth) ====================

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
    
    if (!sessionId) {
      navigate('/', { replace: true });
      return;
    }
    
    const exchangeSession = async () => {
      try {
        const resp = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        if (resp.data.success) {
          navigate('/', { state: { user: resp.data.user }, replace: true });
        } else {
          toast.error("Authentication failed. Please try again.");
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error("Auth session exchange failed:", err?.response?.data || err.message);
        toast.error("Authentication failed. Please try again.");
        navigate('/login', { replace: true });
      }
    };
    
    exchangeSession();
  }, [navigate]);
  
  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-500 spinner" />
    </div>
  );
};

// ==================== CONFIGURATOR WIZARD ====================

const ProductTypeCard = ({ type, selected, onClick }) => (
  <div
    data-testid={`product-type-${type.slug}`}
    onClick={onClick}
    className={`group relative overflow-hidden bg-[#121212] border cursor-pointer transition-all duration-200 p-6
      ${selected ? 'border-blue-500 selection-ring' : 'border-white/10 hover:border-blue-500/50'}`}
  >
    <div className="space-y-3">
      <h3 className="font-mono text-lg font-semibold text-gray-100">{type.name}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{type.description}</p>
      <div className="flex gap-2 flex-wrap">
        {type.has_calculator && (
          <span className="text-xs font-mono px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30">
            CALCULATOR
          </span>
        )}
        {type.has_addons && (
          <span className="text-xs font-mono px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30">
            ADD-ONS
          </span>
        )}
        {type.has_dimension_input && (
          <span className="text-xs font-mono px-2 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30">
            CUSTOM SIZE
          </span>
        )}
      </div>
    </div>
    {selected && (
      <div className="absolute top-3 right-3">
        <Check className="w-5 h-5 text-blue-500" />
      </div>
    )}
  </div>
);

const ModelCard = ({ model, selected, onClick }) => (
  <div
    data-testid={`model-${model.model_number}`}
    onClick={onClick}
    className={`bg-[#121212] border cursor-pointer transition-all duration-200 p-4
      ${selected ? 'border-blue-500 selection-ring' : 'border-white/10 hover:border-blue-500/50'}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <h4 className="font-mono text-base font-semibold text-gray-100">{model.model_number}</h4>
        <p className="text-sm text-gray-400 mt-1">{model.description}</p>
      </div>
      {selected && <Check className="w-5 h-5 text-blue-500" />}
    </div>
  </div>
);

const AddOnCheckbox = ({ addon, checked, onChange }) => (
  <label
    data-testid={`addon-${addon.code}`}
    className={`flex items-start gap-3 p-3 border cursor-pointer transition-all
      ${checked ? 'bg-blue-500/10 border-blue-500/50' : 'bg-[#121212] border-white/10 hover:border-white/20'}`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="custom-checkbox mt-0.5"
    />
    <div className="flex-1">
      <div className="font-mono text-sm font-medium text-gray-100">{addon.name}</div>
      <div className="text-xs text-gray-500 font-mono">{addon.code}</div>
      {addon.description && <div className="text-xs text-gray-400 mt-1">{addon.description}</div>}
    </div>
  </label>
);

// Dimension Input for Reach-in/Walk-in
// Extended Specification Input for Reach-in/Walk-in
const SpecificationInput = ({ specs, onChange, dimensionOffsets, chamberType }) => {
  const hasOffsets = dimensionOffsets && chamberType && dimensionOffsets[chamberType] && 
    (dimensionOffsets[chamberType].width_offset || dimensionOffsets[chamberType].depth_offset || dimensionOffsets[chamberType].height_offset);

  const InputField = ({ label, name, unit, placeholder, readOnly, tag }) => (
    <div className="space-y-1.5">
      <label className="text-xs font-mono uppercase text-gray-500 block">
        {label} {tag && <span className="text-blue-400 normal-case">{tag}</span>}
      </label>
      <div className="flex">
        <input
          type="text"
          value={specs[name] || ''}
          onChange={(e) => !readOnly && onChange({ ...specs, [name]: e.target.value })}
          className={`flex-1 bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-gray-100 
            focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
            ${readOnly ? 'bg-[#0d0d0f] text-gray-400 cursor-not-allowed' : ''}`}
          placeholder={placeholder}
          readOnly={readOnly}
          data-testid={`spec-${name}`}
        />
        {unit && (
          <span className="bg-[#1C1C1F] border border-l-0 border-white/10 px-2 py-2 text-xs font-mono text-gray-500 flex items-center">
            {unit}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="specification-input">
      {/* Internal Dimensions */}
      <div>
        <h4 className="font-mono text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500"></span>
          Internal Dimensions
        </h4>
        <div className="grid sm:grid-cols-3 gap-3">
          <InputField label="Width (Internal)" name="internal_width" unit="mm" placeholder="1000" readOnly tag="{{Internal_Width}}" />
          <InputField label="Depth (Internal)" name="internal_depth" unit="mm" placeholder="1000" readOnly tag="{{Internal_Depth}}" />
          <InputField label="Height (Internal)" name="internal_height" unit="mm" placeholder="1500" readOnly tag="{{Internal_Height}}" />
        </div>
      </div>

      {/* External Dimensions */}
      <div>
        <h4 className="font-mono text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 bg-purple-500"></span>
          External Dimensions {hasOffsets && <span className="text-xs text-green-400 font-normal">(Auto-calculated from offsets)</span>}
        </h4>
        <div className="grid sm:grid-cols-3 gap-3">
          <InputField label="Width (External)" name="external_width" unit="mm" placeholder="1400" readOnly={!!hasOffsets} tag="{{External_Width}}" />
          <InputField label="Depth (External)" name="external_depth" unit="mm" placeholder="1800" readOnly={!!hasOffsets} tag="{{External_Depth}}" />
          <InputField label="Height (External)" name="external_height" unit="mm" placeholder="2200" readOnly={!!hasOffsets} tag="{{External_Height}}" />
        </div>
      </div>

      {/* Temperature Specifications */}
      <div>
        <h4 className="font-mono text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 bg-cyan-500"></span>
          Temperature Specifications
        </h4>
        <div className="grid sm:grid-cols-4 gap-3">
          <InputField label="Min Temp" name="min_temp" unit="°C" placeholder="-40" tag="{{Min_Temp}}" />
          <InputField label="Max Temp" name="max_temp" unit="°C" placeholder="+180" tag="{{Max_Temp}}" />
          <InputField label="Ramp Cool" name="ramp_cool" unit="K/min" placeholder="5" tag="{{Ramp_Cool}}" />
          <InputField label="Ramp Heat" name="ramp_heat" unit="K/min" placeholder="5" tag="{{Ramp_Heat}}" />
        </div>
      </div>

      {/* Load Specifications */}
      <div>
        <h4 className="font-mono text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 bg-yellow-500"></span>
          Load Specifications
        </h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <InputField label="Dead Load" name="dead_load" unit="kg" placeholder="100" tag="{{Dead_Load}}" />
          <InputField label="Live Load" name="live_load" unit="W" placeholder="500" tag="{{Live_Load}}" />
        </div>
      </div>

      {/* Power Specifications */}
      <div>
        <h4 className="font-mono text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500"></span>
          Power Specifications
        </h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <InputField label="Nominal Power" name="nominal_power" placeholder="e.g., 15 kW" tag="{{Nominal_Power}}" />
          <InputField label="Nominal Current" name="nominal_current" placeholder="e.g., 25 A" tag="{{Nominal_Current}}" />
        </div>
      </div>

      {/* Volume */}
      {specs.internal_width && specs.internal_depth && specs.internal_height && (
        <div className="bg-blue-500/10 border border-blue-500/30 p-3">
          <span className="text-xs font-mono uppercase text-blue-400">Calculated Volume </span>
          <span className="font-mono text-blue-400 ml-2">
            {((parseFloat(specs.internal_width) * parseFloat(specs.internal_depth) * parseFloat(specs.internal_height)) / 1000000).toFixed(1)} Liters
          </span>
          <span className="text-xs text-gray-500 ml-2">{'{{Volume}}'}</span>
        </div>
      )}
    </div>
  );
};

const DimensionInput = ({ dimensions, onChange, limits }) => {
  const volume = dimensions.length && dimensions.width && dimensions.height
    ? (dimensions.length * dimensions.width * dimensions.height) / 1000000
    : 0;

  const getRange = (axis) => {
    if (!limits) return null;
    const min = limits[`min_${axis}`];
    const max = limits[`max_${axis}`];
    if (min && max && (min > 0 || max > 0)) return { min, max };
    return null;
  };

  const isOutOfRange = (val, axis) => {
    const range = getRange(axis);
    if (!range || !val) return false;
    if (range.min > 0 && val < range.min) return true;
    if (range.max > 0 && val > range.max) return true;
    return false;
  };

  const RangeHint = ({ axis }) => {
    const range = getRange(axis);
    if (!range) return null;
    return <span className="text-[10px] font-mono text-gray-600 ml-1">({range.min}–{range.max})</span>;
  };

  return (
    <div className="space-y-4" data-testid="dimension-input">
      <h3 className="font-mono text-lg font-semibold text-gray-100">Chamber Dimensions</h3>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase text-gray-500 block">
            Internal Width (mm)<RangeHint axis="width" />
          </label>
          <input
            type="number"
            value={dimensions.length || ''}
            onChange={(e) => onChange({ ...dimensions, length: parseFloat(e.target.value) || 0 })}
            className={`w-full bg-[#09090B] border px-4 py-3 font-mono text-sm text-gray-100 
              focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
              ${isOutOfRange(dimensions.length, 'width') ? 'border-red-500/60' : 'border-white/10'}`}
            placeholder="e.g., 1000"
            min={getRange('width')?.min || undefined}
            max={getRange('width')?.max || undefined}
            data-testid="input-length"
          />
          {isOutOfRange(dimensions.length, 'width') && (
            <span className="text-[10px] font-mono text-red-400">Out of range ({getRange('width').min}–{getRange('width').max})</span>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase text-gray-500 block">
            Internal Depth (mm)<RangeHint axis="depth" />
          </label>
          <input
            type="number"
            value={dimensions.width || ''}
            onChange={(e) => onChange({ ...dimensions, width: parseFloat(e.target.value) || 0 })}
            className={`w-full bg-[#09090B] border px-4 py-3 font-mono text-sm text-gray-100 
              focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
              ${isOutOfRange(dimensions.width, 'depth') ? 'border-red-500/60' : 'border-white/10'}`}
            placeholder="e.g., 1000"
            min={getRange('depth')?.min || undefined}
            max={getRange('depth')?.max || undefined}
            data-testid="input-width"
          />
          {isOutOfRange(dimensions.width, 'depth') && (
            <span className="text-[10px] font-mono text-red-400">Out of range ({getRange('depth').min}–{getRange('depth').max})</span>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase text-gray-500 block">
            Internal Height (mm)<RangeHint axis="height" />
          </label>
          <input
            type="number"
            value={dimensions.height || ''}
            onChange={(e) => onChange({ ...dimensions, height: parseFloat(e.target.value) || 0 })}
            className={`w-full bg-[#09090B] border px-4 py-3 font-mono text-sm text-gray-100 
              focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
              ${isOutOfRange(dimensions.height, 'height') ? 'border-red-500/60' : 'border-white/10'}`}
            placeholder="e.g., 1500"
            min={getRange('height')?.min || undefined}
            max={getRange('height')?.max || undefined}
            data-testid="input-height"
          />
          {isOutOfRange(dimensions.height, 'height') && (
            <span className="text-[10px] font-mono text-red-400">Out of range ({getRange('height').min}–{getRange('height').max})</span>
          )}
        </div>
      </div>
      {volume > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/30 p-4">
          <div className="text-xs font-mono uppercase text-blue-400 mb-1">Calculated Volume</div>
          <div className="font-mono text-2xl text-blue-400" data-testid="calculated-volume">
            {volume.toFixed(1)} Liters
          </div>
        </div>
      )}
    </div>
  );
};

// Heat Load Calculator
const HeatLoadCalculator = ({ dimensions, onCalculate, initialData, selectedKit, onKitSelect }) => {
  const [refTypes, setRefTypes] = useState([]);
  const [formData, setFormData] = useState(initialData || {
    ref_type: "A-Type -40°C",
    ramp_K_per_min: 5,
    product_mass_kg: 100,
    product_live_W: 0,
    cp_product: 0.5,
    min_temp: -40
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRefTypes = async () => {
      try {
        const response = await axios.get(`${API}/refrigeration-types`);
        setRefTypes(response.data);
      } catch (err) {
        setRefTypes(["A-Type -40°C", "B-Type -70°C"]);
      }
    };
    fetchRefTypes();
  }, []);

  // Auto-select refrigeration type based on min temp
  useEffect(() => {
    const minTemp = typeof formData.min_temp === 'string' ? parseFloat(formData.min_temp) : formData.min_temp;
    if (isNaN(minTemp)) return;
    const newType = minTemp < -40 ? "B-Type -70°C" : "A-Type -40°C";
    if (newType !== formData.ref_type) {
      setFormData(prev => ({ ...prev, ref_type: newType }));
    }
  }, [formData.min_temp]);

  const handleCalculate = async () => {
    if (!dimensions.length || !dimensions.width || !dimensions.height) {
      toast.error("Please enter chamber dimensions first");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-heat-load`, {
        ...formData,
        length_mm: dimensions.length,
        width_mm: dimensions.width,
        height_mm: dimensions.height,
        min_temp: formData.min_temp
      });
      setResult(response.data);
      onCalculate(response.data);
      toast.success("Calculation complete");
    } catch (err) {
      toast.error("Calculation failed: " + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="heat-load-calculator">
      <div className="space-y-4">
        <h3 className="font-mono text-lg font-semibold text-gray-100">Heat Load Calculator</h3>
        
        {/* Auto-selected refrigeration type indicator - only shown after calculation */}
        {result && (
          <div className="bg-[#1C1C1F] border border-white/10 px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-gray-500">Refrigeration Type</span>
            <span className="font-mono text-sm text-cyan-400" data-testid="auto-ref-type">{formData.ref_type}</span>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Min Temperature (°C)</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.min_temp}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || val === '-' || /^-?\d*\.?\d*$/.test(val)) {
                  setFormData({ ...formData, min_temp: val });
                }
              }}
              onBlur={(e) => {
                const num = parseFloat(e.target.value);
                if (!isNaN(num)) setFormData(prev => ({ ...prev, min_temp: num }));
              }}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 
                focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              data-testid="input-min-temp"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Ramp Rate (K/min)</label>
            <input
              type="number"
              value={formData.ramp_K_per_min}
              onChange={(e) => setFormData({ ...formData, ramp_K_per_min: parseFloat(e.target.value) || 0 })}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 
                focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              step="0.5"
              data-testid="input-ramp-rate"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Product Mass (kg)</label>
            <input
              type="number"
              value={formData.product_mass_kg}
              onChange={(e) => setFormData({ ...formData, product_mass_kg: parseFloat(e.target.value) || 0 })}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 
                focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              data-testid="input-product-mass"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Product Live Load (W)</label>
            <input
              type="number"
              value={formData.product_live_W}
              onChange={(e) => setFormData({ ...formData, product_live_W: parseFloat(e.target.value) || 0 })}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 
                focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              data-testid="input-live-load"
            />
          </div>
        </div>

        <button
          onClick={handleCalculate}
          disabled={loading || !dimensions.length || !dimensions.width || !dimensions.height}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 font-mono text-sm uppercase tracking-wide
            transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="calculate-heat-load-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 spinner" /> : <Calculator className="w-4 h-4" />}
          Calculate Heat Load
        </button>
      </div>

      {result && (
        <div className="bg-[#121212] border border-white/10 p-6 space-y-4 fade-in" data-testid="calc-result">
          <h4 className="font-mono text-sm uppercase text-gray-500">Calculation Results</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-400">Total Heat Load</span>
              <span className="font-mono text-lg text-blue-400">{result.total_kW} kW</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-400">Heat Load (BTU/hr)</span>
              <span className="font-mono text-gray-200">{result.total_BTUh?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-400">Chamber Volume</span>
              <span className="font-mono text-gray-200">{result.calculated_volume_liters} L</span>
            </div>
          </div>
          
          {result.kits && result.kits.length > 0 && (
            <div className="mt-4">
              <h5 className="text-xs font-mono uppercase text-gray-500 mb-2">Select Refrigeration Kit</h5>
              <div className="space-y-2">
                {result.kits.filter(k => k.is_adequate).map((kit) => (
                  <div
                    key={kit.model}
                    onClick={() => onKitSelect && onKitSelect(kit.model)}
                    className={`p-3 border cursor-pointer transition-all ${
                      selectedKit === kit.model
                        ? 'bg-blue-500/15 border-blue-500/60'
                        : kit.is_recommended
                          ? 'bg-green-500/10 border-green-500/50 hover:border-green-500/80'
                          : 'bg-[#1C1C1F] border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`kit-option-${kit.model}`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="refrigeration-kit"
                          checked={selectedKit === kit.model}
                          readOnly
                          className="accent-blue-500"
                        />
                        <span className="font-mono text-sm text-gray-100">{kit.model}</span>
                        <span className="text-xs text-gray-500">({kit.cap_kW} kW)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedKit === kit.model && (
                          <span className="text-xs font-mono text-blue-400">SELECTED</span>
                        )}
                        {kit.is_recommended && selectedKit !== kit.model && (
                          <span className="text-xs font-mono text-green-400">RECOMMENDED</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TestStandardSearch = ({ onSelect, selectedAddons }) => {
  const [search, setSearch] = useState("");
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStandards = async () => {
      if (search.length < 2) {
        setStandards([]);
        return;
      }
      setLoading(true);
      try {
        const response = await axios.get(`${API}/test-standards?search=${encodeURIComponent(search)}`);
        setStandards(response.data);
      } catch (err) {
        console.error("Failed to fetch standards:", err);
      }
      setLoading(false);
    };

    const debounce = setTimeout(fetchStandards, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  return (
    <div className="space-y-3" data-testid="test-standard-search">
      <label className="text-xs font-mono uppercase text-gray-500 block">Search Test Standards</label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="e.g., ASTM B-117, ISO 9227..."
          className="w-full bg-[#09090B] border border-white/10 pl-10 pr-4 py-3 font-mono text-sm text-gray-100 
            focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          data-testid="standard-search-input"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 spinner" />}
      </div>
      
      {standards.length > 0 && (
        <div className="border border-white/10 max-h-60 overflow-y-auto">
          {standards.map((std) => (
            <div
              key={std.id}
              onClick={() => onSelect(std.addons)}
              className="p-3 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
              data-testid={`standard-${std.standard}`}
            >
              <div className="font-mono text-sm text-gray-100">{std.standard}</div>
              <div className="text-xs text-gray-500 mt-1">
                {std.origin && <span>{std.origin}</span>}
                {std.industry && <span className="ml-2">• {std.industry}</span>}
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">
                {std.addons?.map((addon) => (
                  <span key={addon} className={`text-xs font-mono px-1.5 py-0.5 
                    ${selectedAddons.includes(addon) ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-300'}`}>
                    {addon}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SectionAddonsSelector = ({ productTypeSlug, selected, onChange }) => {
  const [sections, setSections] = useState([]);
  const [sectionAddons, setSectionAddons] = useState({});

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const [secRes, addonsRes] = await Promise.all([
          axios.get(`${API}/addon-sections?product_type_slug=${productTypeSlug}`),
          axios.get(`${API}/addons?product_type_slug=${productTypeSlug}`)
        ]);
        setSections(secRes.data);
        const grouped = {};
        for (const addon of addonsRes.data) {
          const sid = addon.section_id || '__unsectioned';
          if (!grouped[sid]) grouped[sid] = [];
          grouped[sid].push(addon);
        }
        setSectionAddons(grouped);
      } catch (err) {
        console.error("Failed to fetch sections/addons:", err);
      }
    };
    if (productTypeSlug) fetchSections();
  }, [productTypeSlug]);

  if (sections.length === 0) return null;

  return (
    <>
      {sections.map((section) => {
        const items = sectionAddons[section.id] || [];
        if (items.length === 0) return null;
        return (
          <div key={section.id} className="border-t border-white/10 pt-8" data-testid={`section-${section.slug}`}>
            <h3 className="font-mono text-lg font-semibold text-gray-100 mb-4">{section.name}</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {items.map((addon) => (
                <label
                  key={addon.id}
                  className={`flex items-start gap-3 p-3 border cursor-pointer transition-all
                    ${selected.includes(addon.code) ? 'bg-blue-500/10 border-blue-500/50' : 'bg-[#121212] border-white/10 hover:border-white/20'}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(addon.code)}
                    onChange={() => {
                      if (selected.includes(addon.code)) {
                        onChange(selected.filter(c => c !== addon.code));
                      } else {
                        onChange([...selected, addon.code]);
                      }
                    }}
                    className="custom-checkbox mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-mono text-sm font-medium text-gray-100">{addon.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{addon.code}</div>
                    {addon.description && <div className="text-xs text-gray-400 mt-1">{addon.description}</div>}
                  </div>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

const ConfigurationSummary = ({ config, pricing }) => (
  <div className="bg-[#121212] border border-white/10 p-6 space-y-4" data-testid="config-summary">
    <h3 className="font-mono text-lg font-semibold text-gray-100 flex items-center gap-2">
      <FileText className="w-5 h-5" />
      Configuration Summary
    </h3>
    
    <div className="space-y-3">
      <div className="flex justify-between py-2 border-b border-white/5">
        <span className="text-gray-400">Product Type</span>
        <span className="font-mono text-gray-100">{config.product_type}</span>
      </div>
      
      {config.model_number && (
        <div className="flex justify-between py-2 border-b border-white/5">
          <span className="text-gray-400">Model</span>
          <span className="font-mono text-blue-400">{config.model_number}</span>
        </div>
      )}
      
      {config.calculated_volume_liters && (
        <>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-gray-400">Chamber Volume</span>
            <span className="font-mono text-purple-400">{config.calculated_volume_liters.toFixed(1)} Liters</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-gray-400">Dimensions (L×W×H)</span>
            <span className="font-mono text-gray-200">
              {config.length_mm}×{config.width_mm}×{config.height_mm} mm
            </span>
          </div>
        </>
      )}
      
      {config.refrigeration_kit && (
        <div className="flex justify-between py-2 border-b border-white/5">
          <span className="text-gray-400">Refrigeration Kit</span>
          <span className="font-mono text-cyan-400">{config.refrigeration_kit}</span>
        </div>
      )}
      
      {config.addons?.length > 0 && (
        <div className="py-2 border-b border-white/5">
          <span className="text-gray-400 block mb-2">Add-ons</span>
          <div className="flex gap-2 flex-wrap">
            {config.addons.map((addon) => (
              <span key={addon} className="text-xs font-mono px-2 py-1 bg-green-500/20 text-green-400">
                {addon}
              </span>
            ))}
          </div>
        </div>
      )}

      {config.opportunity_name && (
        <div className="flex justify-between py-2 border-b border-white/5">
          <span className="text-gray-400">Linked Opportunity</span>
          <span className="font-mono text-orange-400">{config.opportunity_name}</span>
        </div>
      )}
    </div>

    {/* Pricing Section */}
    {pricing && (
      <div className="mt-6 pt-4 border-t border-white/10">
        <h4 className="font-mono text-sm uppercase text-gray-500 mb-3 flex items-center gap-2">
          <IndianRupee className="w-4 h-4" />
          Pricing Estimate
        </h4>
        
        {pricing.breakdown?.length > 0 && (
          <div className="space-y-2 mb-4">
            {pricing.breakdown.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-gray-400">{item.item}</span>
                <span className="font-mono text-gray-300">₹{Math.round(item.price || 0).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        )}
        
        <div className="bg-green-500/10 border border-green-500/30 p-4">
          <div className="flex justify-between items-center">
            <div className="text-xs font-mono uppercase text-green-400">Estimated Total</div>
            <div className="font-mono text-2xl text-green-400" data-testid="total-price">
              ₹{Math.round(pricing.total_price || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Configuration Profile labels
const Configurator = ({ googleUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [productTypes, setProductTypes] = useState([]);
  const [models, setModels] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [oppSearch, setOppSearch] = useState('');

  // Zoho auth state (additional gate for CPQ)
  const [zohoAuth, setZohoAuth] = useState({
    isAuthenticated: false,
    zoho_user_id: googleUser?.zoho_user_id || ''
  });
  const [showAllOpps, setShowAllOpps] = useState(false);
  const isSuperUser = googleUser?.is_super_user || false;

  // Configuration state
  const [selectedType, setSelectedType] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [dimensions, setDimensions] = useState({ length: 0, width: 0, height: 0 });
  const [dimensionOffsets, setDimensionOffsets] = useState({});
  const [specs, setSpecs] = useState({
    internal_width: '', internal_depth: '', internal_height: '',
    external_width: '', external_depth: '', external_height: '',
    min_temp: '', max_temp: '', ramp_cool: '', ramp_heat: '',
    dead_load: '', live_load: '', nominal_power: '', nominal_current: ''
  });
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [coolingData, setCoolingData] = useState(null);
  const [selectedKitModel, setSelectedKitModel] = useState(null);
  const [configProfile, setConfigProfile] = useState('A');
  const [profileSelected, setProfileSelected] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [walkInConstructionType, setWalkInConstructionType] = useState('panel');

  // Check if user's Google email maps to a Zoho CRM user
  useEffect(() => {
    if (googleUser?.zoho_user_id) {
      setZohoAuth({ isAuthenticated: true, zoho_user_id: googleUser.zoho_user_id });
    }
  }, [googleUser?.zoho_user_id]);

  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const response = await axios.get(`${API}/product-types`);
        setProductTypes(response.data);
      } catch (err) {
        toast.error("Failed to load product types");
      }
      setLoading(false);
    };
    const fetchDimensionOffsets = async () => {
      try {
        const response = await axios.get(`${API}/dimension-offsets`);
        setDimensionOffsets(response.data);
      } catch (err) {
        console.error("Failed to load dimension offsets:", err);
      }
    };
    fetchProductTypes();
    fetchDimensionOffsets();
  }, []);

  // Fetch opportunities when Zoho authenticated
  useEffect(() => {
    if (!zohoAuth.isAuthenticated || !zohoAuth.zoho_user_id) return;
    const fetchOpportunities = async () => {
      try {
        const response = await axios.get(`${API}/zoho/all-opportunities`, { withCredentials: true });
        const opps = response.data.opportunities || [];
        if (isSuperUser && showAllOpps) {
          setOpportunities(opps);
        } else {
          const myOpps = opps.filter(o => o.owner_id === zohoAuth.zoho_user_id);
          setOpportunities(myOpps);
        }
      } catch (err) {
        console.log("Could not fetch opportunities:", err);
      }
    };
    fetchOpportunities();
  }, [zohoAuth.isAuthenticated, zohoAuth.zoho_user_id, isSuperUser, showAllOpps]);

  // Filter opportunities by search
  const filteredOpportunities = opportunities.filter(opp => {
    if (!oppSearch) return true;
    const q = oppSearch.toLowerCase();
    const autoNum = (opp.auto_number || '').toLowerCase();
    const accName = (opp.account_name || '').toLowerCase();
    return autoNum.includes(q) || accName.includes(q);
  });

  const handleZohoLogin = () => {
    // Clear cached user so ProtectedRoute fetches fresh data after Zoho callback
    sessionStorage.removeItem('cme_user');
    window.location.href = `${API}/auth/zoho/login`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {}
    sessionStorage.removeItem('cme_user');
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    if (selectedType) {
      const fetchModels = async () => {
        try {
          const response = await axios.get(`${API}/models?product_type_slug=${selectedType.slug}`);
          setModels(response.data);
        } catch (err) {
          toast.error("Failed to load models");
        }
      };
      const fetchAddons = async () => {
        if (selectedType.has_addons && selectedType.slug === 'salt-spray') {
          try {
            const response = await axios.get(`${API}/addons?product_type_slug=${selectedType.slug}`);
            setAddons(response.data);
          } catch (err) {
            console.error("Failed to load addons:", err);
          }
        }
      };
      fetchModels();
      fetchAddons();
    }
  }, [selectedType]);

  // Calculate pricing when configuration changes
  useEffect(() => {
    const calculatePricing = async () => {
      if (!selectedType) return;
      
      const config = getConfiguration();
      if (!config.model_number && !config.calculated_volume_liters) return;
      
      try {
        const response = await axios.post(`${API}/calculate-price`, config);
        setPricing(response.data);
      } catch (err) {
        console.error("Failed to calculate price:", err);
      }
    };
    
    calculatePricing();
  }, [selectedType, selectedModel, dimensions, selectedAddons, coolingData, configProfile, walkInConstructionType]);

  // Auto-calculate external dimensions from offsets
  useEffect(() => {
    if (!selectedType?.has_dimension_input || !dimensions.length || !dimensions.width || !dimensions.height) return;
    const slug = selectedType.slug;
    let chamberType = slug;
    if (slug === 'walk-in') chamberType = `walk-in-${walkInConstructionType}`;
    const offsets = dimensionOffsets[chamberType];
    if (!offsets) return;
    setSpecs(prev => ({
      ...prev,
      internal_width: String(dimensions.length),
      internal_depth: String(dimensions.width),
      internal_height: String(dimensions.height),
      external_width: offsets.width_offset ? String(dimensions.length + offsets.width_offset) : prev.external_width,
      external_depth: offsets.depth_offset ? String(dimensions.width + offsets.depth_offset) : prev.external_depth,
      external_height: offsets.height_offset ? String(dimensions.height + offsets.height_offset) : prev.external_height,
    }));
  }, [dimensions, selectedType, walkInConstructionType, dimensionOffsets]);

  // Auto-fill specs from cooling data and selected kit
  useEffect(() => {
    if (!coolingData) return;
    setSpecs(prev => ({
      ...prev,
      min_temp: String(coolingData.T_inside_C ?? ''),
      max_temp: String(coolingData.T_initial_C ?? ''),
      ramp_cool: String(coolingData.ramp_K_per_min ?? ''),
      dead_load: String(coolingData.product_mass_kg ?? ''),
      live_load: String(coolingData.product_live_W ?? ''),
    }));
  }, [coolingData]);

  // Auto-fill nominal power/current from selected kit
  useEffect(() => {
    if (!selectedKitModel || !coolingData?.kits) return;
    const kit = coolingData.kits.find(k => k.model === selectedKitModel);
    if (kit && (kit.nominal_power || kit.nominal_current)) {
      setSpecs(prev => ({
        ...prev,
        nominal_power: kit.nominal_power || prev.nominal_power,
        nominal_current: kit.nominal_current || prev.nominal_current,
      }));
    }
  }, [selectedKitModel, coolingData]);

  const handleProductTypeSelect = (type) => {
    if (!selectedOpportunity) {
      toast.error("Please select an Opportunity before proceeding");
      return;
    }
    setSelectedType(type);
    setSelectedModel(null);
    setDimensions({ length: 0, width: 0, height: 0 });
    setSelectedAddons([]);
    setCoolingData(null);
    setSelectedKitModel(null);
    setSpecs({
      internal_width: '', internal_depth: '', internal_height: '',
      external_width: '', external_depth: '', external_height: '',
      min_temp: '', max_temp: '', ramp_cool: '', ramp_heat: '',
      dead_load: '', live_load: '', nominal_power: '', nominal_current: ''
    });
    setPricing(null);
    setWalkInConstructionType('panel');
    setStep(2);
  };

  const handleTestStandardSelect = (addonCodes) => {
    setSelectedAddons(prev => {
      const newAddons = [...new Set([...prev, ...addonCodes])];
      return newAddons;
    });
    toast.success("Add-ons updated based on test standard");
  };

  const handleGenerate = async () => {
    const config = getConfiguration();

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-specification`, config, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const oppName = config.opportunity_name;
      const downloadName = oppName 
        ? `${oppName}-Budgetary_Quote.pptx`
        : `specification_${config.model_number || 'custom'}.pptx`;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PPTX specification generated successfully!");
    } catch (err) {
      toast.error("Failed to generate specification: " + (err.response?.data?.detail || err.message));
    }
    setGenerating(false);
  };

  const getConfiguration = () => {
    const calculatedVolume = dimensions.length && dimensions.width && dimensions.height
      ? (dimensions.length * dimensions.width * dimensions.height) / 1000000
      : null;
    
    return {
      product_type: selectedType?.name || '',
      model_name: selectedModel?.description || '',
      model_number: selectedModel?.model_number || null,
      // Internal dimensions
      length_mm: dimensions.length || parseFloat(specs.internal_width) || null,
      width_mm: dimensions.width || parseFloat(specs.internal_depth) || null,
      height_mm: dimensions.height || parseFloat(specs.internal_height) || null,
      calculated_volume_liters: calculatedVolume,
      // External dimensions
      external_width: parseFloat(specs.external_width) || null,
      external_depth: parseFloat(specs.external_depth) || null,
      external_height: parseFloat(specs.external_height) || null,
      // Temperature specs
      min_temp: parseFloat(specs.min_temp) || (coolingData?.T_inside_C) || null,
      max_temp: parseFloat(specs.max_temp) || (coolingData?.T_initial_C) || null,
      ramp_cool: parseFloat(specs.ramp_cool) || (coolingData?.ramp_K_per_min) || null,
      ramp_heat: parseFloat(specs.ramp_heat) || null,
      // Load specs
      dead_load: parseFloat(specs.dead_load) || (coolingData?.product_mass_kg) || null,
      live_load: parseFloat(specs.live_load) || (coolingData?.product_live_W) || null,
      // Power specs
      nominal_power: specs.nominal_power || null,
      nominal_current: specs.nominal_current || null,
      // Refrigeration (only for product types with calculator/kits)
      refrigeration_type: selectedType?.has_calculator ? (coolingData?.ref_type || null) : null,
      refrigeration_kit: selectedType?.has_calculator ? (selectedKitModel || coolingData?.kits?.find(k => k.is_recommended)?.model || null) : null,
      // Walk-in construction
      walk_in_construction_type: selectedType?.slug === 'walk-in' ? walkInConstructionType : null,
      // Selections
      addons: selectedAddons,
      cooling_data: coolingData,
      // Pricing & CRM
      configuration_profile: configProfile,
      opportunity_id: selectedOpportunity?.id || null,
      opportunity_name: selectedOpportunity?.auto_number || selectedOpportunity?.deal_name || null,
    };
  };

  const canProceedToStep3 = () => {
    if (!selectedType) return false;
    
    // For dimension-based products (Reach-in/Walk-in)
    if (selectedType.has_dimension_input) {
      if (!dimensions.length || !dimensions.width || !dimensions.height) return false;
      if (selectedType.has_calculator && !coolingData) return false;
      return true;
    }
    
    // For model-based products
    if (!selectedModel) return false;
    return true;
  };

  const steps = [
    { num: 1, label: "Product Type" },
    { num: 2, label: "Configuration" },
    { num: 3, label: "Confirm & Generate" }
  ];

  if (!profileSelected) {
    return (
      <div className="min-h-screen bg-[#09090B] flex flex-col" data-testid="profile-selection-gate">
        <header className="border-b border-white/10 bg-[#121212]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white transition-colors" data-testid="profile-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src="/cme-logo.png" alt="CME" className="h-7" />
            <h1 className="text-lg font-mono font-semibold text-white">CPQ</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-lg mx-auto px-6">
            <h2 className="text-2xl font-mono font-bold text-white mb-2">Select Configuration Type</h2>
            <p className="text-sm text-gray-500 font-mono mb-10">Choose a configuration type to proceed</p>
            <div className="grid grid-cols-5 gap-3 max-w-md mx-auto">
              {['A','B','C','D','E','F','G','H','I','J'].map((p) => (
                <button
                  key={p}
                  onClick={() => { setConfigProfile(p); setProfileSelected(true); }}
                  data-testid={`profile-type-${p}`}
                  className="aspect-square flex items-center justify-center font-mono text-lg font-bold
                    border border-white/10 text-gray-400 bg-white/[0.03]
                    hover:border-blue-500 hover:text-blue-400 hover:bg-blue-500/10
                    transition-all duration-150 cursor-pointer"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="configurator">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/cme-logo.png" alt="CME" className="h-8" />
            <h1 className="font-mono text-xl font-bold text-gray-100">CPQ</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-400">
              <User className="w-4 h-4" />
              <span className="text-sm font-mono">{googleUser?.name || googleUser?.email}</span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-xs font-mono text-gray-500 hover:text-gray-300 flex items-center gap-1"
              data-testid="back-to-hub-btn"
            >
              <Home className="w-3 h-3" />
              Tools
            </button>
            <button
              onClick={handleLogout}
              className="text-xs font-mono text-gray-500 hover:text-gray-300 flex items-center gap-1"
              data-testid="logout-btn"
            >
              <LogOut className="w-3 h-3" />
              Logout
            </button>
            <a
              href="/admin"
              className="text-xs font-mono text-gray-500 hover:text-gray-300 flex items-center gap-1"
              data-testid="admin-link"
            >
              <Lock className="w-3 h-3" />
              Admin
            </a>
          </div>
        </div>
      </header>

      {/* Step Indicator */}
      <div className="border-b border-white/10 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            {steps.map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div
                  className={`w-8 h-8 flex items-center justify-center font-mono text-sm border transition-all
                    ${step > s.num ? 'bg-blue-600 border-blue-600 text-white' :
                      step === s.num ? 'border-blue-500 text-blue-500' :
                      'border-white/20 text-gray-500'}`}
                >
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className={`ml-2 font-mono text-sm ${step >= s.num ? 'text-gray-300' : 'text-gray-600'}`}>
                  {s.label}
                </span>
                {idx < steps.length - 1 && (
                  <ChevronRight className={`w-4 h-4 mx-4 ${step > s.num ? 'text-blue-500' : 'text-gray-600'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Step 1: Product Type Selection */}
        {step === 1 && (
          <div className="space-y-6 fade-in" data-testid="step-1">
            {/* CRM Opportunity Selection (Mandatory — requires Zoho auth) */}
            <div className="bg-[#121212] border border-orange-500/30 p-5 mb-6 space-y-4" data-testid="opportunity-section">
              <label className="text-xs font-mono uppercase text-orange-400 block">
                <Building className="w-3 h-3 inline mr-1" />
                Link to CRM Opportunity (Required)
              </label>
              
              {!zohoAuth.isAuthenticated ? (
                <div className="text-center py-4 space-y-3">
                  <p className="text-gray-400 font-mono text-sm">Connect your Zoho CRM account to access opportunities</p>
                  <button
                    onClick={handleZohoLogin}
                    className="bg-[#1F7AE0] hover:bg-[#1868c4] text-white px-6 py-2.5 font-mono text-sm 
                      flex items-center gap-2 mx-auto transition-colors"
                    data-testid="zoho-login-btn"
                  >
                    <Building className="w-4 h-4" />
                    Connect Zoho CRM
                  </button>
                  <p className="text-gray-600 font-mono text-xs">Your email ({googleUser?.email}) will be matched to your CRM profile</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Super User: All/My toggle */}
                  {isSuperUser && (
                    <div className="flex items-center gap-2 mb-2" data-testid="super-user-toggle">
                      <span className="text-xs font-mono text-gray-500">View:</span>
                      <button
                        onClick={() => { setShowAllOpps(false); setSelectedOpportunity(null); }}
                        className={`text-xs font-mono px-3 py-1 border transition-all
                          ${!showAllOpps ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-white/10 text-gray-500 hover:text-gray-300'}`}
                        data-testid="toggle-my-opps"
                      >
                        My Opportunities
                      </button>
                      <button
                        onClick={() => { setShowAllOpps(true); setSelectedOpportunity(null); }}
                        className={`text-xs font-mono px-3 py-1 border transition-all
                          ${showAllOpps ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-white/10 text-gray-500 hover:text-gray-300'}`}
                        data-testid="toggle-all-opps"
                      >
                        All Opportunities
                      </button>
                    </div>
                  )}
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      value={oppSearch}
                      onChange={(e) => setOppSearch(e.target.value)}
                      placeholder="Search by Opportunity No. or Company..."
                      className="w-full bg-[#09090B] border border-white/10 pl-10 pr-4 py-2.5 font-mono text-sm text-gray-100 
                        focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 placeholder-gray-600"
                      data-testid="opportunity-search"
                    />
                  </div>
                  <select
                    value={selectedOpportunity?.id || ''}
                    onChange={(e) => {
                      const opp = filteredOpportunities.find(o => o.id === e.target.value);
                      setSelectedOpportunity(opp || null);
                    }}
                    className={`w-full bg-[#09090B] border px-4 py-2.5 font-mono text-sm text-gray-100 
                      focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500
                      ${!selectedOpportunity ? 'border-red-500/50' : 'border-white/10'}`}
                    data-testid="opportunity-select"
                  >
                    <option value="">-- Select Opportunity --</option>
                    {filteredOpportunities.map((opp) => (
                      <option key={opp.id} value={opp.id}>
                        {opp.auto_number || opp.deal_name} — {opp.account_name}
                      </option>
                    ))}
                  </select>
                  {!selectedOpportunity && (
                    <p className="text-xs font-mono text-red-400">Please select an opportunity to proceed</p>
                  )}
                  {filteredOpportunities.length === 0 && (
                    <p className="text-xs font-mono text-gray-500">No opportunities found for your account</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <h2 className="font-mono text-2xl font-semibold text-gray-100">Select Product Type</h2>
              <p className="text-gray-400 mt-1">Choose the environmental test chamber category</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {productTypes.map((type) => (
                <ProductTypeCard
                  key={type.id}
                  type={type}
                  selected={selectedType?.id === type.id}
                  onClick={() => handleProductTypeSelect(type)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Configuration */}
        {step === 2 && selectedType && (
          <div className="space-y-8 fade-in" data-testid="step-2">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-mono text-2xl font-semibold text-gray-100">
                  {selectedType.name} Configuration
                </h2>
                <p className="text-gray-400 mt-1">
                  {selectedType.has_dimension_input 
                    ? "Enter dimensions and configure options" 
                    : "Select model and configure options"}
                </p>
              </div>
              <button
                onClick={() => setStep(1)}
                className="text-gray-400 hover:text-white flex items-center gap-1 font-mono text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            </div>

            {/* Dimension Input for Reach-in/Walk-in */}
            {selectedType.has_dimension_input && (
              <DimensionInput 
                dimensions={dimensions} 
                onChange={setDimensions} 
                limits={dimensionOffsets[selectedType.slug === 'walk-in' ? `walk-in-${walkInConstructionType}` : selectedType.slug]}
              />
            )}

            {/* Walk-in Construction Type Selector */}
            {selectedType.slug === 'walk-in' && (
              <div className="space-y-2" data-testid="walk-in-construction-type">
                <h3 className="font-mono text-lg font-semibold text-gray-100">Construction Type</h3>
                <div className="flex gap-4">
                  {[
                    { value: 'panel', label: 'Panel Type' },
                    { value: 'welded', label: 'Welded Type' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setWalkInConstructionType(opt.value)}
                      className={`flex-1 py-3 px-4 font-mono text-sm border transition-all
                        ${walkInConstructionType === opt.value 
                          ? 'border-blue-500 bg-blue-500/10 text-blue-400' 
                          : 'border-white/10 text-gray-400 hover:border-white/20'}`}
                      data-testid={`construction-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Model Selection for non-dimension products */}
            {!selectedType.has_dimension_input && (
              <div className="space-y-4">
                <h3 className="font-mono text-lg font-semibold text-gray-100">Select Model</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {models.map((model) => (
                    <ModelCard
                      key={model.id}
                      model={model}
                      selected={selectedModel?.id === model.id}
                      onClick={() => setSelectedModel(model)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Heat Load Calculator for Reach-in / Walk-in */}
            {selectedType.has_calculator && selectedType.has_dimension_input && dimensions.length > 0 && dimensions.width > 0 && dimensions.height > 0 && (
              <div className="border-t border-white/10 pt-8">
                <HeatLoadCalculator
                  dimensions={dimensions}
                  onCalculate={(data) => {
                    setCoolingData(data);
                    const rec = data?.kits?.find(k => k.is_recommended);
                    if (rec) setSelectedKitModel(rec.model);
                  }}
                  initialData={coolingData}
                  selectedKit={selectedKitModel}
                  onKitSelect={setSelectedKitModel}
                />
              </div>
            )}

            {/* Salt Spray Add-ons */}
            {selectedType.slug === 'salt-spray' && selectedModel && (
              <div className="border-t border-white/10 pt-8 space-y-6">
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="font-mono text-lg font-semibold text-gray-100">Select Add-ons</h3>
                    <div className="space-y-2">
                      {addons.map((addon) => (
                        <AddOnCheckbox
                          key={addon.id}
                          addon={addon}
                          checked={selectedAddons.includes(addon.code)}
                          onChange={() => {
                            if (selectedAddons.includes(addon.code)) {
                              setSelectedAddons(selectedAddons.filter(a => a !== addon.code));
                            } else {
                              setSelectedAddons([...selectedAddons, addon.code]);
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <TestStandardSearch
                    onSelect={handleTestStandardSelect}
                    selectedAddons={selectedAddons}
                  />
                </div>
              </div>
            )}

            {/* Dynamic Section Add-ons for Reach-in / Walk-in */}
            {(selectedType.slug === 'reach-in' || selectedType.slug === 'walk-in') && coolingData && (
              <SectionAddonsSelector
                productTypeSlug={selectedType.slug}
                selected={selectedAddons}
                onChange={setSelectedAddons}
              />
            )}

            {/* Specification Details for Reach-in / Walk-in - positioned at the very end */}
            {(selectedType.slug === 'reach-in' || selectedType.slug === 'walk-in') && coolingData && (
              <div className="border-t border-white/10 pt-8">
                <h3 className="font-mono text-lg font-semibold text-gray-100 mb-4">
                  Specification Details (for PPTX Template)
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Fill in additional specifications to populate template placeholders. Fields will auto-fill from calculator where possible.
                </p>
                <SpecificationInput 
                  specs={specs} 
                  onChange={setSpecs} 
                  dimensionOffsets={dimensionOffsets}
                  chamberType={selectedType.slug === 'walk-in' ? `walk-in-${walkInConstructionType}` : selectedType.slug}
                />
              </div>
            )}

            {/* Continue Button */}
            <div className="flex justify-end pt-4">
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedToStep3()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500
                  text-white px-8 py-3 font-mono text-sm uppercase tracking-wide transition-colors
                  flex items-center gap-2"
                data-testid="continue-to-step-3"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="space-y-8 fade-in" data-testid="step-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-mono text-2xl font-semibold text-gray-100">Confirm Configuration</h2>
                <p className="text-gray-400 mt-1">Review pricing and generate specification document</p>
              </div>
              <button
                onClick={() => setStep(2)}
                className="text-gray-400 hover:text-white flex items-center gap-1 font-mono text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            </div>

            <ConfigurationSummary config={getConfiguration()} pricing={pricing} />

            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setStep(1);
                  setSelectedType(null);
                  setSelectedModel(null);
                  setDimensions({ length: 0, width: 0, height: 0 });
                  setSpecs({
                    internal_width: '', internal_depth: '', internal_height: '',
                    external_width: '', external_depth: '', external_height: '',
                    min_temp: '', max_temp: '', ramp_cool: '', ramp_heat: '',
                    dead_load: '', live_load: '', nominal_power: '', nominal_current: ''
                  });
                  setSelectedAddons([]);
                  setCoolingData(null);
                  setPricing(null);
                }}
                className="bg-[#1C1C1F] hover:bg-[#27272A] text-gray-300 px-6 py-3 font-mono text-sm 
                  uppercase tracking-wide transition-colors border border-white/10"
              >
                Start Over
              </button>
              
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500
                  text-white px-8 py-3 font-mono text-sm uppercase tracking-wide transition-colors
                  flex items-center gap-2"
                data-testid="generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 spinner" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Generate PPTX
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ==================== ADMIN PANEL (Simplified for brevity - keeping existing structure) ====================

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.token) {
        localStorage.setItem('cpq_admin_token', response.data.token);
      }
      onLogin();
    } catch (err) {
      setError("Invalid password");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center" data-testid="admin-login">
      <div className="bg-[#121212] border border-white/10 p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-mono text-xl font-bold text-gray-100">Admin Access</h1>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 
                focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter admin password"
              data-testid="admin-password-input"
            />
          </div>
          
          {error && (
            <div className="text-red-400 text-sm font-mono">{error}</div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700
              text-white px-6 py-3 font-mono text-sm uppercase tracking-wide transition-colors
              flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 spinner" /> : null}
            Login
          </button>
        </form>
        
        <div className="mt-6 pt-4 border-t border-white/10">
          <a href="/" className="text-xs font-mono text-gray-500 hover:text-gray-300 flex items-center gap-1">
            <Home className="w-3 h-3" />
            Back to Configurator
          </a>
        </div>
      </div>
    </div>
  );
};


const RefKitRow = ({ kit, refType, onSaved }) => {
  const [price, setPrice] = useState(kit.base_price || 0);
  const [power, setPower] = useState(kit.nominal_power || '');
  const [current, setCurrent] = useState(kit.nominal_current || '');
  const [saving, setSaving] = useState(false);
  const [hasPptx, setHasPptx] = useState(!!kit.pptx_file_path);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/refrigeration-kits/${kit.id || 'new'}`, {
        ref_type: refType,
        model: kit.model,
        cap_kW: kit.cap_kW,
        base_price: parseFloat(price) || 0,
        nominal_power: power,
        nominal_current: current
      });
      toast.success(`${kit.model} updated`);
      onSaved();
    } catch (err) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const handlePptxUpload = async (file) => {
    if (!file?.name.endsWith('.pptx')) { toast.error("Only PPTX files"); return; }
    if (!kit.id) { toast.error("Save the kit first to upload PPTX"); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/upload-entity-pptx/refrigeration-kits/${kit.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("PPTX uploaded");
      setHasPptx(true);
      onSaved();
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const handlePptxRemove = async () => {
    try {
      await axios.delete(`${API}/delete-entity-pptx/refrigeration-kits/${kit.id}`);
      toast.success("PPTX removed");
      setHasPptx(false);
      onSaved();
    } catch (err) {
      toast.error("Remove failed");
    }
  };

  return (
    <tr className="border-t border-white/5 table-row">
      <td className="py-2 px-4 text-sm text-gray-400">{refType}</td>
      <td className="py-2 px-4 font-mono text-sm text-cyan-400">{kit.model}</td>
      <td className="py-2 px-4 text-sm text-gray-300">{kit.cap_kW} kW</td>
      <td className="py-2 px-4">
        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)}
          className="w-24 bg-[#09090B] border border-white/10 px-2 py-1 font-mono text-xs text-green-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-testid={`kit-price-${kit.model}`} />
      </td>
      <td className="py-2 px-4">
        <input type="text" value={power} onChange={(e) => setPower(e.target.value)} placeholder="e.g., 15 kW"
          className="w-24 bg-[#09090B] border border-white/10 px-2 py-1 font-mono text-xs text-yellow-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-testid={`kit-power-${kit.model}`} />
      </td>
      <td className="py-2 px-4">
        <input type="text" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="e.g., 25 A"
          className="w-24 bg-[#09090B] border border-white/10 px-2 py-1 font-mono text-xs text-orange-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-testid={`kit-current-${kit.model}`} />
      </td>
      <td className="py-2 px-4 text-xs font-mono">
        {kit.id ? (
          hasPptx ? (
            <div className="flex items-center gap-1">
              <span className="text-purple-400 flex items-center gap-0.5"><Presentation className="w-3 h-3" /> PPTX</span>
              <button onClick={handlePptxRemove} className="text-red-400 hover:text-red-300 ml-1">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <label className="text-gray-500 hover:text-blue-400 cursor-pointer flex items-center gap-1">
              <Upload className="w-3 h-3" /> Upload
              <input type="file" accept=".pptx" className="hidden" onChange={(e) => { if (e.target.files[0]) handlePptxUpload(e.target.files[0]); }} />
            </label>
          )
        ) : (
          <span className="text-gray-600">—</span>
        )}
      </td>
      <td className="py-2 px-4 text-right">
        <button onClick={handleSave} disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 text-white px-3 py-1 font-mono text-xs"
          data-testid={`kit-save-${kit.model}`}>
          {saving ? '...' : 'Save'}
        </button>
      </td>
    </tr>
  );
};

const ChamberPricingCard = ({ pricing, onSave }) => {
  const defaultTiers = [
    { min_liters: 0, max_liters: 500, price_per_liter: 0 },
    { min_liters: 500, max_liters: 1000, price_per_liter: 0 },
    { min_liters: 1000, max_liters: 99999, price_per_liter: 0 }
  ];
  const [value, setValue] = useState(pricing.price_per_liter);
  const [tiers, setTiers] = useState(
    pricing.price_tiers?.length ? pricing.price_tiers : []
  );
  const [widthOffset, setWidthOffset] = useState(pricing.width_offset || 0);
  const [depthOffset, setDepthOffset] = useState(pricing.depth_offset || 0);
  const [heightOffset, setHeightOffset] = useState(pricing.height_offset || 0);
  const [minWidth, setMinWidth] = useState(pricing.min_width || 0);
  const [maxWidth, setMaxWidth] = useState(pricing.max_width || 0);
  const [minDepth, setMinDepth] = useState(pricing.min_depth || 0);
  const [maxDepth, setMaxDepth] = useState(pricing.max_depth || 0);
  const [minHeight, setMinHeight] = useState(pricing.min_height || 0);
  const [maxHeight, setMaxHeight] = useState(pricing.max_height || 0);
  const [saving, setSaving] = useState(false);

  const hasTiers = tiers.length > 0;

  const enableTiers = () => {
    setTiers(defaultTiers.map(t => ({ ...t, price_per_liter: parseFloat(value) || 0 })));
  };

  const disableTiers = () => {
    setTiers([]);
  };

  const updateTier = (idx, field, val) => {
    const updated = [...tiers];
    updated[idx] = { ...updated[idx], [field]: parseFloat(val) || 0 };
    setTiers(updated);
  };

  const addTier = () => {
    const lastMax = tiers.length ? tiers[tiers.length - 1].max_liters : 0;
    setTiers([...tiers, { min_liters: lastMax, max_liters: lastMax + 500, price_per_liter: 0 }]);
  };

  const removeTier = (idx) => {
    setTiers(tiers.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/chamber-pricing/${pricing.chamber_type}`, {
        price_per_liter: parseFloat(value),
        price_tiers: tiers,
        width_offset: parseFloat(widthOffset),
        depth_offset: parseFloat(depthOffset),
        height_offset: parseFloat(heightOffset),
        min_width: parseFloat(minWidth),
        max_width: parseFloat(maxWidth),
        min_depth: parseFloat(minDepth),
        max_depth: parseFloat(maxDepth),
        min_height: parseFloat(minHeight),
        max_height: parseFloat(maxHeight),
      });
      toast.success(`${pricing.label} updated`);
      onSave();
    } catch (err) {
      toast.error("Failed to update");
    }
    setSaving(false);
  };

  const handlePptxUpload = async (file) => {
    if (!file?.name.endsWith('.pptx')) { toast.error("Only PPTX files"); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/upload-entity-pptx/chamber-pricing/${pricing.chamber_type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("PPTX uploaded");
      onSave();
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const handlePptxRemove = async () => {
    try {
      await axios.delete(`${API}/delete-entity-pptx/chamber-pricing/${pricing.chamber_type}`);
      toast.success("PPTX removed");
      onSave();
    } catch (err) {
      toast.error("Remove failed: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="bg-[#121212] border border-white/10 p-5 space-y-3" data-testid={`chamber-pricing-${pricing.chamber_type}`}>
      <h3 className="font-mono text-sm font-semibold text-gray-200">{pricing.label}</h3>
      
      {/* Pricing */}
      <div>
        {!hasTiers ? (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-mono uppercase text-gray-500">Flat Price per Liter (INR)</label>
              <button onClick={enableTiers} className="text-[10px] font-mono text-blue-400 hover:text-blue-300">
                + Enable Tiered Pricing
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 font-mono text-sm">₹</span>
              <input type="number" value={value} onChange={(e) => setValue(e.target.value)}
                className="flex-1 bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-green-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid={`chamber-price-input-${pricing.chamber_type}`} />
              <span className="text-gray-500 font-mono text-xs">/L</span>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono uppercase text-gray-500">Tiered Pricing (by Volume Range)</label>
              <button onClick={disableTiers} className="text-[10px] font-mono text-red-400 hover:text-red-300">
                Switch to Flat
              </button>
            </div>
            <div className="space-y-2">
              {tiers.map((tier, idx) => (
                <div key={idx} className="flex items-center gap-1.5" data-testid={`tier-${idx}-${pricing.chamber_type}`}>
                  <input type="number" value={tier.min_liters} onChange={(e) => updateTier(idx, 'min_liters', e.target.value)}
                    className="w-16 bg-[#09090B] border border-white/10 px-1.5 py-1 font-mono text-[11px] text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <span className="text-gray-600 text-[10px]">to</span>
                  <input type="number" value={tier.max_liters} onChange={(e) => updateTier(idx, 'max_liters', e.target.value)}
                    className="w-16 bg-[#09090B] border border-white/10 px-1.5 py-1 font-mono text-[11px] text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <span className="text-gray-600 text-[10px]">L →</span>
                  <span className="text-gray-400 text-[10px]">₹</span>
                  <input type="number" value={tier.price_per_liter} onChange={(e) => updateTier(idx, 'price_per_liter', e.target.value)}
                    className="w-20 bg-[#09090B] border border-white/10 px-1.5 py-1 font-mono text-[11px] text-green-400 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <span className="text-gray-600 text-[10px]">/L</span>
                  <button onClick={() => removeTier(idx)} className="text-red-400 hover:text-red-300 ml-1">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button onClick={addTier} className="text-[10px] font-mono text-blue-400 hover:text-blue-300">+ Add Tier</button>
            </div>
            <input type="hidden" value={value} />
          </div>
        )}
      </div>

      {/* Dimension Offsets */}
      <div className="border-t border-white/5 pt-3">
        <label className="text-xs font-mono uppercase text-gray-500 block mb-2">Dimension Offsets (mm) — External = Internal + Offset</label>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] font-mono text-gray-600 block">Width +</label>
            <input type="number" value={widthOffset} onChange={(e) => setWidthOffset(e.target.value)}
              className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-purple-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid={`offset-w-${pricing.chamber_type}`} />
          </div>
          <div>
            <label className="text-[10px] font-mono text-gray-600 block">Depth +</label>
            <input type="number" value={depthOffset} onChange={(e) => setDepthOffset(e.target.value)}
              className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-purple-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid={`offset-d-${pricing.chamber_type}`} />
          </div>
          <div>
            <label className="text-[10px] font-mono text-gray-600 block">Height +</label>
            <input type="number" value={heightOffset} onChange={(e) => setHeightOffset(e.target.value)}
              className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-purple-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid={`offset-h-${pricing.chamber_type}`} />
          </div>
        </div>
      </div>

      {/* Min/Max Dimension Limits */}
      <div className="border-t border-white/5 pt-3">
        <label className="text-xs font-mono uppercase text-gray-500 block mb-2">Dimension Limits (mm) — Min / Max per axis</label>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono text-gray-600 block">Min Width</label>
              <input type="number" value={minWidth} onChange={(e) => setMinWidth(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-cyan-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid={`min-w-${pricing.chamber_type}`} />
            </div>
            <div>
              <label className="text-[10px] font-mono text-gray-600 block">Max Width</label>
              <input type="number" value={maxWidth} onChange={(e) => setMaxWidth(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-cyan-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid={`max-w-${pricing.chamber_type}`} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono text-gray-600 block">Min Depth</label>
              <input type="number" value={minDepth} onChange={(e) => setMinDepth(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-cyan-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid={`min-d-${pricing.chamber_type}`} />
            </div>
            <div>
              <label className="text-[10px] font-mono text-gray-600 block">Max Depth</label>
              <input type="number" value={maxDepth} onChange={(e) => setMaxDepth(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-cyan-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid={`max-d-${pricing.chamber_type}`} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono text-gray-600 block">Min Height</label>
              <input type="number" value={minHeight} onChange={(e) => setMinHeight(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-cyan-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid={`min-h-${pricing.chamber_type}`} />
            </div>
            <div>
              <label className="text-[10px] font-mono text-gray-600 block">Max Height</label>
              <input type="number" value={maxHeight} onChange={(e) => setMaxHeight(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-2 py-1.5 font-mono text-xs text-cyan-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid={`max-h-${pricing.chamber_type}`} />
            </div>
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-700 text-white py-2 font-mono text-xs uppercase tracking-wide transition-colors"
        data-testid={`chamber-price-save-${pricing.chamber_type}`}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>

      {/* PPTX Upload */}
      <div className="border-t border-white/5 pt-3 flex items-center justify-between">
        <span className="text-xs font-mono text-gray-600 uppercase">PPTX Template</span>
        {pricing.pptx_file_path ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-purple-400 flex items-center gap-1"><Presentation className="w-3 h-3" /> Uploaded</span>
            <button data-testid={`pptx-delete-chamber-${pricing.chamber_type}`}
              onClick={(e) => { e.stopPropagation(); handlePptxRemove(); }}
              className="w-5 h-5 flex items-center justify-center text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <label className="text-xs font-mono text-gray-500 hover:text-blue-400 cursor-pointer flex items-center gap-1">
            <Upload className="w-3 h-3" /> Upload PPTX
            <input type="file" accept=".pptx" className="hidden" onChange={(e) => { if (e.target.files[0]) handlePptxUpload(e.target.files[0]); }} />
          </label>
        )}
      </div>
    </div>
  );
};


const SuperUsersTab = ({ users, onRefresh }) => {
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/super-users`, { email: newEmail.trim() });
      toast.success(`Added ${newEmail.trim()} as super user`);
      setNewEmail('');
      onRefresh();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add super user';
      toast.error(msg);
    }
    setAdding(false);
  };

  const handleRemove = async (email) => {
    try {
      await axios.delete(`${API}/super-users/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to remove super user');
    }
  };

  return (
    <div className="space-y-6" data-testid="super-users-tab">
      <div>
        <h2 className="font-mono text-lg text-gray-100">Super Users</h2>
        <p className="text-gray-500 text-sm">Super users can view all CRM opportunities, not just their own.</p>
      </div>

      {/* Add new */}
      <div className="flex gap-2">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="email@cmenvirosystems.com"
          className="flex-1 bg-[#09090B] border border-white/10 px-4 py-2.5 font-mono text-sm text-gray-100 
            focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          data-testid="super-user-email-input"
        />
        <button
          onClick={handleAdd}
          disabled={adding || !newEmail.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 font-mono text-sm flex items-center gap-2"
          data-testid="super-user-add-btn"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      {/* List */}
      <div className="border border-white/10 overflow-hidden">
        {users.length === 0 ? (
          <p className="text-gray-500 text-sm font-mono py-6 text-center">No super users configured</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#1C1C1F]">
                <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Email</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((su) => (
                <tr key={su.email} className="border-t border-white/5">
                  <td className="py-3 px-4 font-mono text-sm text-gray-300">{su.email}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleRemove(su.email)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`remove-super-user-${su.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const DemoAdminsTab = ({ admins, onRefresh }) => {
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/demo/admins`, { email: newEmail.trim() });
      toast.success(`Added ${newEmail.trim()} as demo admin`);
      setNewEmail('');
      onRefresh();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add demo admin';
      toast.error(msg);
    }
    setAdding(false);
  };

  const handleRemove = async (email) => {
    try {
      await axios.delete(`${API}/demo/admins/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to remove demo admin');
    }
  };

  return (
    <div className="space-y-6" data-testid="demo-admins-tab">
      <div>
        <h2 className="font-mono text-lg text-gray-100">Demo Admins</h2>
        <p className="text-gray-500 text-sm">Demo admins can manage screens and hotspots in the enviCoM 4.0 demo. Others only see the demo view.</p>
      </div>

      <div className="flex gap-2">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="email@cmenvirosystems.com"
          className="flex-1 bg-[#09090B] border border-white/10 px-4 py-2.5 font-mono text-sm text-gray-100 
            focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          data-testid="demo-admin-email-input"
        />
        <button
          onClick={handleAdd}
          disabled={adding || !newEmail.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 font-mono text-sm flex items-center gap-2"
          data-testid="demo-admin-add-btn"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      <div className="border border-white/10 overflow-hidden">
        {admins.length === 0 ? (
          <p className="text-gray-500 text-sm font-mono py-6 text-center">No demo admins configured</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#1C1C1F]">
                <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Email</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.email} className="border-t border-white/5">
                  <td className="py-3 px-4 font-mono text-sm text-gray-300">{admin.email}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleRemove(admin.email)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`remove-demo-admin-${admin.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const PresAdminsTab = ({ admins, onRefresh }) => {
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/pres-builder/admins`, { email: newEmail.trim() });
      toast.success(`Added ${newEmail.trim()} as presentation admin`);
      setNewEmail('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
    setAdding(false);
  };

  const handleRemove = async (email) => {
    try {
      await axios.delete(`${API}/pres-builder/admins/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="space-y-6" data-testid="pres-admins-tab">
      <div>
        <h2 className="font-mono text-lg text-gray-100">Presentation Admins</h2>
        <p className="text-gray-500 text-sm">Presentation admins can manage sections, upload templates, and set bookend pages in the Presentation Builder.</p>
      </div>
      <div className="flex gap-2">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="email@cmenvirosystems.com"
          className="flex-1 bg-[#09090B] border border-white/10 px-4 py-2.5 font-mono text-sm text-gray-100 
            focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          data-testid="pres-admin-email-input"
        />
        <button
          onClick={handleAdd}
          disabled={adding || !newEmail.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 font-mono text-sm flex items-center gap-2"
          data-testid="pres-admin-add-btn"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      <div className="border border-white/10 overflow-hidden">
        {admins.length === 0 ? (
          <p className="text-gray-500 text-sm font-mono py-6 text-center">No presentation admins configured</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#1C1C1F]">
                <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Email</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.email} className="border-t border-white/5">
                  <td className="py-3 px-4 font-mono text-sm text-gray-300">{admin.email}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleRemove(admin.email)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`remove-pres-admin-${admin.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const EnvivomAdminsTab = ({ admins, onRefresh }) => {
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/envivom/admins`, { email: newEmail.trim() });
      toast.success(`Added ${newEmail.trim()} as enviVoM admin`);
      setNewEmail('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
    setAdding(false);
  };

  const handleRemove = async (email) => {
    try {
      await axios.delete(`${API}/envivom/admins/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="space-y-6" data-testid="envivom-admins-tab">
      <div>
        <h2 className="font-mono text-lg text-gray-100">enviCoM SF Admins</h2>
        <p className="text-gray-500 text-sm">enviCoM SF admins can manage screens, upload media, and configure hotspots in the enviCoM SF Demo.</p>
      </div>
      <div className="flex gap-2">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="email@cmenvirosystems.com"
          className="flex-1 bg-[#09090B] border border-white/10 px-4 py-2.5 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          data-testid="envivom-admin-email-input"
        />
        <button onClick={handleAdd} disabled={adding || !newEmail.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 font-mono text-sm flex items-center gap-2"
          data-testid="envivom-admin-add-btn"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      <div className="border border-white/10 overflow-hidden">
        {admins.length === 0 ? (
          <p className="text-gray-500 text-sm font-mono py-6 text-center">No enviVoM admins configured</p>
        ) : (
          <table className="w-full">
            <thead><tr className="bg-[#1C1C1F]"><th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Email</th><th className="w-20"></th></tr></thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.email} className="border-t border-white/5">
                  <td className="py-3 px-4 font-mono text-sm text-gray-300">{admin.email}</td>
                  <td className="py-3 px-4">
                    <button onClick={() => handleRemove(admin.email)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const LevitoIotAdminsTab = ({ admins, onRefresh }) => {
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try { await axios.post(`${API}/levito-iot/admins`, { email: newEmail.trim() }); toast.success(`Added ${newEmail.trim()}`); setNewEmail(''); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setAdding(false);
  };
  const handleRemove = async (email) => {
    try { await axios.delete(`${API}/levito-iot/admins/${encodeURIComponent(email)}`); toast.success(`Removed ${email}`); onRefresh(); }
    catch { toast.error('Failed to remove'); }
  };
  return (
    <div className="space-y-6" data-testid="levito-iot-admins-tab">
      <div><h2 className="font-mono text-lg text-gray-100">Levito IOT Admins</h2><p className="text-gray-500 text-sm">Manage who can edit the Levito IOT demo.</p></div>
      <div className="flex gap-2">
        <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@cmenvirosystems.com"
          className="flex-1 bg-[#09090B] border border-white/10 px-4 py-2.5 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
        <button onClick={handleAdd} disabled={adding || !newEmail.trim()} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 font-mono text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="border border-white/10 overflow-hidden">
        {admins.length === 0 ? <p className="text-gray-500 text-sm font-mono py-6 text-center">No Levito IOT admins configured</p> : (
          <table className="w-full"><thead><tr className="bg-[#1C1C1F]"><th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Email</th><th className="w-20"></th></tr></thead>
            <tbody>{admins.map(a => (<tr key={a.email} className="border-t border-white/5"><td className="py-3 px-4 font-mono text-sm text-gray-300">{a.email}</td><td className="py-3 px-4"><button onClick={() => handleRemove(a.email)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button></td></tr>))}</tbody></table>
        )}
      </div>
    </div>
  );
};

const LevitoFsAdminsTab = ({ admins, onRefresh }) => {
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try { await axios.post(`${API}/levito-fs/admins`, { email: newEmail.trim() }); toast.success(`Added ${newEmail.trim()}`); setNewEmail(''); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setAdding(false);
  };
  const handleRemove = async (email) => {
    try { await axios.delete(`${API}/levito-fs/admins/${encodeURIComponent(email)}`); toast.success(`Removed ${email}`); onRefresh(); }
    catch { toast.error('Failed to remove'); }
  };
  return (
    <div className="space-y-6" data-testid="levito-fs-admins-tab">
      <div><h2 className="font-mono text-lg text-gray-100">Levito Field Service Admins</h2><p className="text-gray-500 text-sm">Manage who can edit the Levito Field Service demo.</p></div>
      <div className="flex gap-2">
        <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@cmenvirosystems.com"
          className="flex-1 bg-[#09090B] border border-white/10 px-4 py-2.5 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
        <button onClick={handleAdd} disabled={adding || !newEmail.trim()} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 font-mono text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="border border-white/10 overflow-hidden">
        {admins.length === 0 ? <p className="text-gray-500 text-sm font-mono py-6 text-center">No Levito Field Service admins configured</p> : (
          <table className="w-full"><thead><tr className="bg-[#1C1C1F]"><th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Email</th><th className="w-20"></th></tr></thead>
            <tbody>{admins.map(a => (<tr key={a.email} className="border-t border-white/5"><td className="py-3 px-4 font-mono text-sm text-gray-300">{a.email}</td><td className="py-3 px-4"><button onClick={() => handleRemove(a.email)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button></td></tr>))}</tbody></table>
        )}
      </div>
    </div>
  );
};

const RefDocsAdminsTab = ({ admins, onRefresh }) => {
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try { await axios.post(`${API}/ref-docs/admins`, { email: newEmail.trim() }); toast.success(`Added ${newEmail.trim()}`); setNewEmail(''); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setAdding(false);
  };
  const handleRemove = async (email) => {
    try { await axios.delete(`${API}/ref-docs/admins/${encodeURIComponent(email)}`); toast.success(`Removed ${email}`); onRefresh(); }
    catch { toast.error('Failed to remove'); }
  };
  return (
    <div className="space-y-6" data-testid="ref-docs-admins-tab">
      <div><h2 className="font-mono text-lg text-gray-100">Reference Docs Admins</h2><p className="text-gray-500 text-sm">Manage who can upload and organize reference documents.</p></div>
      <div className="flex gap-2">
        <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@cmenvirosystems.com"
          className="flex-1 bg-[#09090B] border border-white/10 px-4 py-2.5 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-600"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
        <button onClick={handleAdd} disabled={adding || !newEmail.trim()} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 font-mono text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="border border-white/10 overflow-hidden">
        {admins.length === 0 ? <p className="text-gray-500 text-sm font-mono py-6 text-center">No Ref Docs admins configured</p> : (
          <table className="w-full"><thead><tr className="bg-[#1C1C1F]"><th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Email</th><th className="w-20"></th></tr></thead>
            <tbody>{admins.map(a => (<tr key={a.email} className="border-t border-white/5"><td className="py-3 px-4 font-mono text-sm text-gray-300">{a.email}</td><td className="py-3 px-4"><button onClick={() => handleRemove(a.email)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button></td></tr>))}</tbody></table>
        )}
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState("models");
  const [data, setData] = useState({
    models: [],
    productTypes: [],
    addons: [],
    templates: [],
    testStandards: [],
    addonSections: [],
    refrigerationKits: [],
    volumePricing: [],
    chamberPricing: [],
    superUsers: [],
    demoAdmins: [],
    presAdmins: [],
    envivomAdmins: [],
    levitoIotAdmins: [],
    levitoFsAdmins: [],
    refDocsAdmins: []
  });
  const [loading, setLoading] = useState(false);

  // Modals
  const [showModelModal, setShowModelModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showKitModal, setShowKitModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStandardModal, setShowStandardModal] = useState(false);
  const [showProductTypeModal, setShowProductTypeModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await axios.delete(`${API}/addon-sections/${sectionId}`);
      toast.success('Section deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete section');
    }
  };

  const handlePptxUpload = async (entityType, entityId, file) => {
    if (!file?.name.endsWith('.pptx')) { toast.error("Only PPTX files"); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/upload-entity-pptx/${entityType}/${entityId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("PPTX uploaded");
      fetchData();
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const handlePptxDelete = async (entityType, entityId) => {
    try {
      await axios.delete(`${API}/delete-entity-pptx/${entityType}/${entityId}`);
      toast.success("PPTX removed");
      fetchData();
    } catch (err) {
      toast.error("Remove failed: " + (err.response?.data?.detail || err.message));
      console.error("PPTX delete error:", err);
    }
  };

  const PptxUploadCell = ({ entityType, entityId, filePath }) => (
    <div className="flex items-center gap-2">
      {filePath ? (
        <>
          <span className="text-xs font-mono text-purple-400 flex items-center gap-1"><Presentation className="w-3 h-3" /> PPTX</span>
          <button
            data-testid={`pptx-delete-${entityType}-${entityId}`}
            onClick={(e) => { e.stopPropagation(); handlePptxDelete(entityType, entityId); }}
            className="w-5 h-5 flex items-center justify-center text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
            title="Remove PPTX"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </>
      ) : (
        <label className="text-xs font-mono text-gray-600 hover:text-blue-400 cursor-pointer flex items-center gap-1">
          <Upload className="w-3 h-3" /> Upload
          <input type="file" accept=".pptx" className="hidden" onChange={(e) => {
            if (e.target.files[0]) handlePptxUpload(entityType, entityId, e.target.files[0]);
          }} />
        </label>
      )}
    </div>
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [modelsRes, typesRes, addonsRes, templatesRes, standardsRes, sectionsRes, kitsRes, volRes, chamberRes, superRes, demoAdminsRes, presAdminsRes, envivomAdminsRes, levitoIotAdminsRes, levitoFsAdminsRes, refDocsAdminsRes] = await Promise.all([
        axios.get(`${API}/models`),
        axios.get(`${API}/product-types`),
        axios.get(`${API}/addons`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/test-standards`),
        axios.get(`${API}/addon-sections`),
        axios.get(`${API}/refrigeration-kits`),
        axios.get(`${API}/volume-pricing`),
        axios.get(`${API}/chamber-pricing`),
        axios.get(`${API}/super-users`),
        axios.get(`${API}/demo/admins`),
        axios.get(`${API}/pres-builder/admins`),
        axios.get(`${API}/envivom/admins`),
        axios.get(`${API}/levito-iot/admins`),
        axios.get(`${API}/levito-fs/admins`),
        axios.get(`${API}/ref-docs/admins`)
      ]);
      setData({
        models: modelsRes.data,
        productTypes: typesRes.data,
        addons: addonsRes.data,
        templates: templatesRes.data,
        testStandards: standardsRes.data,
        addonSections: sectionsRes.data,
        refrigerationKits: kitsRes.data,
        volumePricing: volRes.data,
        chamberPricing: chamberRes.data,
        superUsers: superRes.data,
        demoAdmins: demoAdminsRes.data,
        presAdmins: presAdminsRes.data,
        envivomAdmins: envivomAdminsRes.data,
        levitoIotAdmins: levitoIotAdminsRes.data,
        levitoFsAdmins: levitoFsAdminsRes.data,
        refDocsAdmins: refDocsAdminsRes.data
      });
    } catch (err) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchData();
    }
  }, [authenticated, fetchData]);

  const handleSeedData = async () => {
    if (!window.confirm("This will reset all data. Continue?")) return;
    setLoading(true);
    try {
      await axios.post(`${API}/seed-data`);
      toast.success("Data seeded successfully");
      fetchData();
    } catch (err) {
      toast.error("Failed to seed data");
    }
    setLoading(false);
  };

  const handleDelete = async (endpoint, id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/${endpoint}/${id}`);
      toast.success("Deleted successfully");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  if (!authenticated) {
    return <AdminLogin onLogin={() => setAuthenticated(true)} />;
  }

  const tabs = [
    { id: "productTypes", label: "Product Types" },
    { id: "models", label: "Models" },
    { id: "sections", label: "Sections" },
    { id: "addons", label: "Add-ons" },
    { id: "standards", label: "Test Standards" },
    { id: "templates", label: "PPTX Templates" },
    { id: "kits", label: "Refrigeration Kits" },
    { id: "pricing", label: "Chamber Pricing" },
    { id: "superusers", label: "Super Users" },
    { id: "demoadmins", label: "Demo Admins" },
    { id: "presadmins", label: "Pres Admins" },
    { id: "envivomadmins", label: "enviCoM SF Admins" },
    { id: "levitoiotadmins", label: "Levito IOT Admins" },
    { id: "levitofsadmins", label: "Levito FS Admins" },
    { id: "refdocsadmins", label: "Ref Docs Admins" }
  ];

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-panel">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-mono text-xl font-bold text-gray-100">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleSeedData}
              className="text-xs font-mono text-yellow-500 hover:text-yellow-400"
              data-testid="seed-data-btn"
            >
              Seed Initial Data
            </button>
            <a href="/" className="text-xs font-mono text-gray-500 hover:text-gray-300 flex items-center gap-1">
              <Home className="w-3 h-3" />
              Configurator
            </a>
            <button
              onClick={() => setAuthenticated(false)}
              className="text-xs font-mono text-gray-500 hover:text-gray-300 flex items-center gap-1"
            >
              <LogOut className="w-3 h-3" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-[#0f0f0f] overflow-x-auto">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-4 font-mono text-xs uppercase tracking-wide transition-colors border-b-2 whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'text-blue-400 border-blue-500'
                    : 'text-gray-500 border-transparent hover:text-gray-300'}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 spinner" />
          </div>
        ) : (
          <>
            {/* Product Types Tab */}
            {activeTab === "productTypes" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-mono text-lg text-gray-100">Product Types ({data.productTypes.length})</h2>
                  <button
                    onClick={() => { setEditingItem(null); setShowProductTypeModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-mono text-sm flex items-center gap-2"
                    data-testid="add-product-type-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Product Type
                  </button>
                </div>
                <div className="border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#1C1C1F]">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Name</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Slug</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Description</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Flags</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">PPTX</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.productTypes.map((pt) => (
                        <tr key={pt.id} className="border-t border-white/5 table-row">
                          <td className="py-3 px-4 font-mono text-sm text-blue-400">{pt.name}</td>
                          <td className="py-3 px-4 font-mono text-xs text-gray-400">{pt.slug}</td>
                          <td className="py-3 px-4 text-sm text-gray-300">{pt.description}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1 flex-wrap">
                              {pt.has_addons && <span className="text-xs font-mono px-1.5 py-0.5 bg-green-500/20 text-green-400">Addons</span>}
                              {pt.has_calculator && <span className="text-xs font-mono px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400">Calc</span>}
                              {pt.has_dimension_input && <span className="text-xs font-mono px-1.5 py-0.5 bg-amber-500/20 text-amber-400">Dims</span>}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <PptxUploadCell entityType="product-types" entityId={pt.id} filePath={pt.pptx_file_path} />
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => { setEditingItem(pt); setShowProductTypeModal(true); }} className="text-gray-400 hover:text-white mr-3">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete('product-types', pt.id)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Models Tab */}
            {activeTab === "models" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-mono text-lg text-gray-100">Product Models ({data.models.length})</h2>
                  <button
                    onClick={() => { setEditingItem(null); setShowModelModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-mono text-sm flex items-center gap-2"
                    data-testid="add-model-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Model
                  </button>
                </div>
                <div className="border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#1C1C1F]">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Model</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Type</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Description</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Base Price</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">PPTX</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.models.map((model) => (
                        <tr key={model.id} className="border-t border-white/5 table-row">
                          <td className="py-3 px-4 font-mono text-sm text-blue-400">{model.model_number}</td>
                          <td className="py-3 px-4 text-sm text-gray-400">{model.product_type_slug}</td>
                          <td className="py-3 px-4 text-sm text-gray-300">{model.description}</td>
                          <td className="py-3 px-4 text-sm text-green-400 text-right font-mono">
                            ₹{model.base_price?.toLocaleString('en-IN')}
                          </td>
                          <td className="py-3 px-4">
                            <PptxUploadCell entityType="models" entityId={model.id} filePath={model.pptx_file_path} />
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => { setEditingItem(model); setShowModelModal(true); }} className="text-gray-400 hover:text-white mr-3">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete('models', model.id)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Test Standards Tab */}
            {activeTab === "standards" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-mono text-lg text-gray-100">Test Standards ({data.testStandards.length})</h2>
                  <button
                    onClick={() => { setEditingItem(null); setShowStandardModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-mono text-sm flex items-center gap-2"
                    data-testid="add-standard-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Standard
                  </button>
                </div>
                <div className="border border-white/10 overflow-hidden max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-[#1C1C1F]">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Standard</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Origin</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Industry</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Add-ons</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.testStandards.map((std) => (
                        <tr key={std.id} className="border-t border-white/5 table-row">
                          <td className="py-3 px-4 font-mono text-sm text-gray-100">{std.standard}</td>
                          <td className="py-3 px-4 text-sm text-gray-400">{std.origin}</td>
                          <td className="py-3 px-4 text-sm text-gray-400">{std.industry}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1 flex-wrap">
                              {std.addons?.map((addon) => (
                                <span key={addon} className="text-xs font-mono px-1.5 py-0.5 bg-green-500/20 text-green-400">
                                  {addon}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => { setEditingItem(std); setShowStandardModal(true); }} className="text-gray-400 hover:text-white mr-3">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete('test-standards', std.id)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Sections Tab */}
            {activeTab === "sections" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-mono text-lg text-gray-100">Add-on Sections ({data.addonSections.length})</h2>
                  <button
                    onClick={() => { setEditingItem(null); setShowSectionModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-mono text-sm flex items-center gap-2"
                    data-testid="add-section-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Section
                  </button>
                </div>
                <div className="border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#1C1C1F]">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Name</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Slug</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Product Type</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Sort Order</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.addonSections.map((section) => (
                        <tr key={section.id} className="border-t border-white/5 table-row">
                          <td className="py-3 px-4 font-mono text-sm text-purple-400">{section.name}</td>
                          <td className="py-3 px-4 font-mono text-sm text-gray-300">{section.slug}</td>
                          <td className="py-3 px-4">
                            <span className="text-xs font-mono px-1.5 py-0.5 bg-blue-500/20 text-blue-400">{section.product_type_slug || '—'}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-400 text-right font-mono">{section.sort_order}</td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => { setEditingItem(section); setShowSectionModal(true); }} className="text-gray-400 hover:text-white mr-3">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteSection(section.id)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Add-ons Tab */}
            {activeTab === "addons" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-mono text-lg text-gray-100">Add-ons ({data.addons.length})</h2>
                  <button
                    onClick={() => { setEditingItem(null); setShowAddonModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-mono text-sm flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> Add Add-on
                  </button>
                </div>
                <div className="border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#1C1C1F]">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Name</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Code</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Type</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Section</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Base Price</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">PPTX</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.addons.map((addon) => (
                        <tr key={addon.id} className="border-t border-white/5 table-row">
                          <td className="py-3 px-4 font-mono text-sm text-green-400">{addon.name}</td>
                          <td className="py-3 px-4 font-mono text-sm text-gray-300">{addon.code}</td>
                          <td className="py-3 px-4 text-sm">
                            <div className="flex gap-1 flex-wrap">
                              {(addon.product_type_slugs || [addon.product_type_slug]).filter(Boolean).map((slug) => (
                                <span key={slug} className="text-xs font-mono px-1.5 py-0.5 bg-blue-500/20 text-blue-400">{slug}</span>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-purple-400 font-mono">
                            {data.addonSections.find(s => s.id === addon.section_id)?.name || '—'}
                          </td>
                          <td className="py-3 px-4 text-sm text-green-400 text-right font-mono">
                            ₹{addon.base_price?.toLocaleString('en-IN')}
                          </td>
                          <td className="py-3 px-4">
                            <PptxUploadCell entityType="addons" entityId={addon.id} filePath={addon.pptx_file_path} />
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => { setEditingItem(addon); setShowAddonModal(true); }} className="text-gray-400 hover:text-white mr-3">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete('addons', addon.id)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === "templates" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-mono text-lg text-gray-100">PPTX Templates ({data.templates.length})</h2>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-mono text-sm flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" /> Upload Template
                  </button>
                </div>
                <div className="border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#1C1C1F]">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Name</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">File</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Category</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.templates.length === 0 ? (
                        <tr><td colSpan={4} className="py-8 text-center text-gray-500">No templates uploaded</td></tr>
                      ) : (
                        data.templates.map((t) => (
                          <tr key={t.id} className="border-t border-white/5 table-row">
                            <td className="py-3 px-4 font-mono text-sm text-purple-400">{t.name}</td>
                            <td className="py-3 px-4 text-sm text-gray-300">{t.filename}</td>
                            <td className="py-3 px-4 text-sm text-gray-400">{t.category}</td>
                            <td className="py-3 px-4 text-right">
                              <button onClick={() => handleDelete('templates', t.id)} className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Refrigeration Kits Tab */}
            {activeTab === "kits" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-mono text-lg text-gray-100">Refrigeration Kits</h2>
                    <p className="text-gray-500 text-sm">Edit price, nominal power and nominal current. Use "Add Kit" to add custom kits.</p>
                  </div>
                  <button
                    onClick={() => { setEditingItem(null); setShowKitModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-mono text-sm flex items-center gap-2"
                    data-testid="add-kit-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Kit
                  </button>
                </div>
                <div className="border border-white/10 overflow-hidden max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-[#1C1C1F]">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Type</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Model</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Capacity</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Base Price</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Nom. Power</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">Nom. Current</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-400">PPTX</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-400">Save</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(data.refrigerationKits).map(([type, kits]) => (
                        kits.map((kit) => (
                          <RefKitRow key={`${type}-${kit.model}`} kit={kit} refType={type} onSaved={fetchData} />
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Chamber Pricing Tab */}
            {activeTab === "pricing" && (
              <div className="space-y-8">
                <div>
                  <h2 className="font-mono text-lg text-gray-100">Chamber Pricing (per Liter)</h2>
                  <p className="text-gray-500 text-sm">Set the price per liter for each chamber type. This is multiplied by the calculated volume.</p>
                </div>

                {/* Editable Chamber Pricing Cards */}
                <div className="grid sm:grid-cols-3 gap-4">
                  {data.chamberPricing.map((cp) => (
                    <ChamberPricingCard key={cp.chamber_type} pricing={cp} onSave={fetchData} />
                  ))}
                </div>

                {/* Legacy Volume Pricing Tiers (read-only reference) */}
                {data.volumePricing.length > 0 && (
                  <div className="space-y-3 mt-8">
                    <h3 className="font-mono text-sm text-gray-500 uppercase">Legacy Volume Tiers (Reference)</h3>
                    <div className="border border-white/5 overflow-hidden opacity-60">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-[#1C1C1F]">
                            <th className="text-left py-2 px-4 font-mono text-xs uppercase text-gray-500">Min Vol (L)</th>
                            <th className="text-left py-2 px-4 font-mono text-xs uppercase text-gray-500">Max Vol (L)</th>
                            <th className="text-right py-2 px-4 font-mono text-xs uppercase text-gray-500">Price/L (INR)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.volumePricing.map((vp, idx) => (
                            <tr key={idx} className="border-t border-white/5">
                              <td className="py-2 px-4 font-mono text-xs text-gray-400">{vp.min_volume}</td>
                              <td className="py-2 px-4 font-mono text-xs text-gray-400">{vp.max_volume}</td>
                              <td className="py-2 px-4 text-xs text-gray-400 text-right font-mono">₹{vp.price_per_liter?.toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Super Users Tab */}
            {activeTab === "superusers" && (
              <SuperUsersTab users={data.superUsers} onRefresh={fetchData} />
            )}
            {/* Demo Admins Tab */}
            {activeTab === "demoadmins" && (
              <DemoAdminsTab admins={data.demoAdmins} onRefresh={fetchData} />
            )}
            {/* Pres Admins Tab */}
            {activeTab === "presadmins" && (
              <PresAdminsTab admins={data.presAdmins} onRefresh={fetchData} />
            )}
            {/* enviVoM Admins Tab */}
            {activeTab === "envivomadmins" && (
              <EnvivomAdminsTab admins={data.envivomAdmins} onRefresh={fetchData} />
            )}
            {/* Levito IOT Admins Tab */}
            {activeTab === "levitoiotadmins" && (
              <LevitoIotAdminsTab admins={data.levitoIotAdmins} onRefresh={fetchData} />
            )}
            {/* Levito FS Admins Tab */}
            {activeTab === "levitofsadmins" && (
              <LevitoFsAdminsTab admins={data.levitoFsAdmins} onRefresh={fetchData} />
            )}
            {/* Ref Docs Admins Tab */}
            {activeTab === "refdocsadmins" && (
              <RefDocsAdminsTab admins={data.refDocsAdmins} onRefresh={fetchData} />
            )}
          </>
        )}
      </main>

      {/* Modals */}
      {showModelModal && (
        <ModelModal
          model={editingItem}
          productTypes={data.productTypes}
          onClose={() => setShowModelModal(false)}
          onSave={() => { setShowModelModal(false); fetchData(); }}
        />
      )}

      {showAddonModal && (
        <AddonModal
          addon={editingItem}
          productTypes={data.productTypes}
          addonSections={data.addonSections}
          onClose={() => setShowAddonModal(false)}
          onSave={() => { setShowAddonModal(false); fetchData(); }}
        />
      )}

      {showSectionModal && (
        <SectionModal
          section={editingItem}
          productTypes={data.productTypes}
          onClose={() => setShowSectionModal(false)}
          onSave={() => { setShowSectionModal(false); fetchData(); }}
        />
      )}

      {showStandardModal && (
        <TestStandardModal
          standard={editingItem}
          onClose={() => setShowStandardModal(false)}
          onSave={() => { setShowStandardModal(false); fetchData(); }}
        />
      )}

      {showUploadModal && (
        <UploadModal
          productTypes={data.productTypes}
          onClose={() => setShowUploadModal(false)}
          onSave={() => { setShowUploadModal(false); fetchData(); }}
        />
      )}

      {showProductTypeModal && (
        <ProductTypeModal
          productType={editingItem}
          onClose={() => setShowProductTypeModal(false)}
          onSave={() => { setShowProductTypeModal(false); fetchData(); }}
        />
      )}

      {showKitModal && (
        <KitModal
          onClose={() => setShowKitModal(false)}
          onSave={() => { setShowKitModal(false); fetchData(); }}
        />
      )}
    </div>
  );
};

// ==================== MODALS ====================

const ModelModal = ({ model, productTypes, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    model_number: model?.model_number || "",
    product_type_slug: model?.product_type_slug || "",
    description: model?.description || "",
    base_price: model?.base_price || 0
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (model) {
        await axios.put(`${API}/models/${model.id}`, { ...model, ...formData });
        toast.success("Model updated");
      } else {
        await axios.post(`${API}/models`, formData);
        toast.success("Model created");
      }
      onSave();
    } catch (err) {
      toast.error("Failed to save: " + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-lg text-gray-100">{model ? "Edit Model" : "Add Model"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Model Number</label>
              <input type="text" value={formData.model_number} onChange={(e) => setFormData({ ...formData, model_number: e.target.value })} required
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Base Price (INR)</label>
              <input type="number" value={formData.base_price} onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Product Type</label>
            <select value={formData.product_type_slug} onChange={(e) => setFormData({ ...formData, product_type_slug: e.target.value })} required
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="">Select type</option>
              {productTypes.map((type) => (<option key={type.id} value={type.slug}>{type.name}</option>))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 font-mono text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-mono text-sm flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 spinner" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddonModal = ({ addon, productTypes, addonSections, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: addon?.name || "",
    code: addon?.code || "",
    product_type_slugs: addon?.product_type_slugs || (addon?.product_type_slug ? [addon.product_type_slug] : []),
    section_id: addon?.section_id || "",
    description: addon?.description || "",
    base_price: addon?.base_price || 0
  });
  const [loading, setLoading] = useState(false);

  const applicableSections = (addonSections || []).filter(s => 
    !formData.product_type_slugs.length || formData.product_type_slugs.includes(s.product_type_slug)
  );

  const handleToggleType = (slug) => {
    const current = formData.product_type_slugs;
    if (current.includes(slug)) {
      setFormData({ ...formData, product_type_slugs: current.filter(s => s !== slug), section_id: '' });
    } else {
      setFormData({ ...formData, product_type_slugs: [...current, slug] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (addon) {
        await axios.put(`${API}/addons/${addon.id}`, { ...addon, ...formData });
        toast.success("Add-on updated");
      } else {
        await axios.post(`${API}/addons`, formData);
        toast.success("Add-on created");
      }
      onSave();
    } catch (err) {
      toast.error("Failed to save");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-lg text-gray-100">{addon ? "Edit Add-on" : "Add Add-on"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Code</label>
              <input type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} required
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Applicable Product Types</label>
            <div className="flex gap-2 flex-wrap">
              {productTypes.map((type) => (
                <label key={type.id}
                  className={`flex items-center gap-2 px-3 py-2 border cursor-pointer font-mono text-xs transition-colors
                    ${formData.product_type_slugs.includes(type.slug) ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-[#09090B] border-white/10 text-gray-400'}`}>
                  <input type="checkbox" checked={formData.product_type_slugs.includes(type.slug)}
                    onChange={() => handleToggleType(type.slug)} className="custom-checkbox" />
                  {type.name}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Section</label>
              <select value={formData.section_id} onChange={(e) => setFormData({ ...formData, section_id: e.target.value })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">No Section</option>
                {applicableSections.map((sec) => (<option key={sec.id} value={sec.id}>{sec.name}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Base Price (INR)</label>
              <input type="number" value={formData.base_price} onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 font-mono text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-mono text-sm flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 spinner" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SectionModal = ({ section, productTypes, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: section?.name || "",
    slug: section?.slug || "",
    sort_order: section?.sort_order || 0,
    product_type_slug: section?.product_type_slug || (section?.applicable_product_type_slugs?.[0] || "")
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formData, slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-') };
      if (section) {
        await axios.put(`${API}/addon-sections/${section.id}`, { ...section, ...payload });
        toast.success("Section updated");
      } else {
        await axios.post(`${API}/addon-sections`, payload);
        toast.success("Section created");
      }
      onSave();
    } catch (err) {
      toast.error("Failed to save section");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-lg text-gray-100">{section ? "Edit Section" : "Add Section"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Section Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., Battery Test Features" data-testid="section-name-input" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Slug</label>
              <input type="text" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="auto-generated" data-testid="section-slug-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Product Type</label>
              <select value={formData.product_type_slug} onChange={(e) => setFormData({ ...formData, product_type_slug: e.target.value })} required
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Select type</option>
                {productTypes.map((type) => (<option key={type.id} value={type.slug}>{type.name}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Sort Order</label>
              <input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 font-mono text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-mono text-sm flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 spinner" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const KitModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    ref_type: "A-Type -40°C",
    model: "",
    cap_kW: 0,
    base_price: 0,
    nominal_power: "",
    nominal_current: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/refrigeration-kits`, {
        ...formData,
        cap_kW: parseFloat(formData.cap_kW) || 0,
        base_price: parseFloat(formData.base_price) || 0
      });
      toast.success("Kit created");
      onSave();
    } catch (err) {
      toast.error("Failed to create kit");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-lg text-gray-100">Add Refrigeration Kit</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Refrigeration Type</label>
              <select value={formData.ref_type} onChange={(e) => setFormData({ ...formData, ref_type: e.target.value })} required
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="A-Type -40°C">A-Type -40°C</option>
                <option value="B-Type -70°C">B-Type -70°C</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Model Name</label>
              <input type="text" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} required
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., A55" data-testid="kit-model-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Capacity (kW)</label>
              <input type="number" value={formData.cap_kW} onChange={(e) => setFormData({ ...formData, cap_kW: e.target.value })} required step="0.1"
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Base Price (INR)</label>
              <input type="number" value={formData.base_price} onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Nom. Power</label>
              <input type="text" value={formData.nominal_power} onChange={(e) => setFormData({ ...formData, nominal_power: e.target.value })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., 15 kW" />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 font-mono text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-mono text-sm flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 spinner" />} Create Kit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TestStandardModal = ({ standard, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    standard: standard?.standard || "",
    origin: standard?.origin || "",
    industry: standard?.industry || "",
    procedure: standard?.procedure || "",
    addons: standard?.addons?.join(', ') || ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...formData,
      addons: formData.addons.split(',').map(a => a.trim()).filter(a => a)
    };
    try {
      if (standard) {
        await axios.put(`${API}/test-standards/${standard.id}`, { ...standard, ...payload });
        toast.success("Standard updated");
      } else {
        await axios.post(`${API}/test-standards`, payload);
        toast.success("Standard created");
      }
      onSave();
    } catch (err) {
      toast.error("Failed to save");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-lg text-gray-100">{standard ? "Edit Test Standard" : "Add Test Standard"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Standard Name</label>
            <input type="text" value={formData.standard} onChange={(e) => setFormData({ ...formData, standard: e.target.value })} required
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g., ASTM B-117" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Origin</label>
              <input type="text" value={formData.origin} onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., American, German" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase text-gray-500 block">Industry</label>
              <input type="text" value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., GM, VW, Ford" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Add-on Codes (comma separated)</label>
            <input type="text" value={formData.addons} onChange={(e) => setFormData({ ...formData, addons: e.target.value })}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g., DCH, SO2, REF20" />
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 font-mono text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-mono text-sm flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 spinner" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ProductTypeModal = ({ productType, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: productType?.name || "",
    slug: productType?.slug || "",
    description: productType?.description || "",
    has_addons: productType?.has_addons || false,
    has_calculator: productType?.has_calculator || false,
    has_dimension_input: productType?.has_dimension_input || false,
  });
  const [loading, setLoading] = useState(false);

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: productType ? prev.slug : name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (productType) {
        await axios.put(`${API}/product-types/${productType.id}`, { ...productType, ...formData });
        toast.success("Product type updated");
      } else {
        await axios.post(`${API}/product-types`, formData);
        toast.success("Product type created");
      }
      onSave();
    } catch (err) {
      toast.error("Failed to save");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-lg text-gray-100">{productType ? "Edit Product Type" : "Add Product Type"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Name</label>
            <input type="text" value={formData.name} onChange={handleNameChange} required
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g., Salt Spray" data-testid="pt-name-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Slug</label>
            <input type="text" value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              required
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g., salt-spray" data-testid="pt-slug-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Description</label>
            <input type="text" value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Optional description" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-mono uppercase text-gray-500 block">Flags</label>
            <div className="flex gap-4">
              {[
                { key: 'has_addons', label: 'Has Add-ons' },
                { key: 'has_calculator', label: 'Has Calculator' },
                { key: 'has_dimension_input', label: 'Dimension Input' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                    className="accent-blue-500" />
                  <span className="font-mono text-xs">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 font-mono text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-mono text-sm flex items-center gap-2"
              data-testid="pt-save-btn">
              {loading && <Loader2 className="w-4 h-4 spinner" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UploadModal = ({ productTypes, onClose, onSave }) => {
  const [formData, setFormData] = useState({ name: "", category: "product", product_type_slug: "", model_number: "", addon_code: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please select a file"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.keys(formData).forEach(key => fd.append(key, formData[key]));
      await axios.post(`${API}/templates/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("Template uploaded");
      onSave();
    } catch (err) {
      toast.error("Failed to upload");
    }
    setLoading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f?.name.endsWith('.pptx')) {
      setFile(f);
      if (!formData.name) setFormData({ ...formData, name: f.name.replace('.pptx', '') });
    } else {
      toast.error("Only PPTX files allowed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-lg text-gray-100">Upload PPTX Template</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className={`drop-zone p-8 text-center cursor-pointer ${dragOver ? 'dragover' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}>
            <input id="file-input" type="file" accept=".pptx" onChange={(e) => {
              const f = e.target.files[0];
              if (f) { setFile(f); if (!formData.name) setFormData({ ...formData, name: f.name.replace('.pptx', '') }); }
            }} className="hidden" />
            <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
            {file ? <p className="font-mono text-sm text-blue-400">{file.name}</p> : <p className="text-gray-400 text-sm">Drop PPTX file here</p>}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Template Name</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase text-gray-500 block">Category</label>
            <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 font-mono text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="product">Product Type</option>
              <option value="model">Specific Model</option>
              <option value="addon">Add-on</option>
              <option value="general">General</option>
            </select>
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 font-mono text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={loading || !file} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-mono text-sm flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 spinner" />} Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== LOGIN PAGE ====================

const LoginPage = () => {
  const navigate = useNavigate();
  
  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (resp.data?.email) {
          navigate('/', { state: { user: resp.data }, replace: true });
        }
      } catch (err) {
        // Not authenticated
      }
    };
    checkAuth();
  }, [navigate]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a2332' }} data-testid="login-page">
      <div className="bg-white rounded-xl shadow-2xl p-10 w-full max-w-md text-center">
        <div className="mb-6">
          <img src="/cme-logo.png" alt="CME" className="h-14 mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Sales Tools</h2>
        <p className="text-gray-500 text-sm mb-8">Sign in to access Sales kits, Calculators, CPQ</p>
        <button
          onClick={handleGoogleLogin}
          className="w-full bg-[#4285F4] hover:bg-[#3574d4] text-white py-3 px-6 rounded-lg text-base
            flex items-center justify-center gap-3 transition-colors"
          data-testid="google-login-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
        <p className="text-gray-400 text-xs mt-6">Access restricted to @cmenvirosystems.com users</p>
      </div>
    </div>
  );
};

// ==================== APP HUB ====================

const AppHub = ({ user }) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {}
    sessionStorage.removeItem('cme_user');
    navigate('/login', { replace: true });
  };

  const apps = [
    {
      id: 'heat-load',
      name: 'Heat Load Calculator',
      description: 'Calculate cooling requirements and refrigeration kits for environmental test chambers',
      icon: Calculator,
      color: 'bg-emerald-600',
      action: () => navigate('/heat-load', { state: { user } }),
      external: false
    },
    {
      id: 'cpq',
      name: 'CME CPQ',
      description: 'Configure, Price, and Quote environmental test chambers with automated spec generation',
      icon: FileText,
      color: 'bg-blue-600',
      action: () => navigate('/cpq', { state: { user } }),
      external: false
    },
    {
      id: 'spec-builder',
      name: 'Technical Spec Builder',
      description: 'Build custom specifications by merging PPTX templates from your library',
      icon: FileStack,
      color: 'bg-violet-600',
      action: () => navigate('/spec-builder', { state: { user } }),
      external: false
    },
    {
      id: 'envicom',
      name: 'enviCoM 4.0 Demo',
      description: 'Interactive controller demo — showcase the enviCoM 4.0 interface to customers',
      icon: Monitor,
      color: 'bg-cyan-600',
      action: () => navigate('/envicom', { state: { user } }),
      external: false
    },
    {
      id: 'ramp-calc',
      name: 'Ramp Calculator',
      description: 'Compare IEC, Average, and Linear temperature ramp methods with animated graphs',
      icon: Activity,
      color: 'bg-amber-600',
      action: () => navigate('/ramp-calculator', { state: { user } }),
      external: false
    },
    {
      id: 'pres-builder',
      name: 'Presentation Builder',
      description: 'Build custom presentations by selecting and merging PPTX templates from your library',
      icon: Layers,
      color: 'bg-teal-600',
      action: () => navigate('/presentation-builder', { state: { user } }),
      external: false
    },
    {
      id: 'envivom',
      name: 'enviCoM SF Demo',
      description: 'Interactive controller demo — showcase the enviCoM SF interface to customers',
      icon: Monitor,
      color: 'bg-violet-600',
      action: () => navigate('/envivom', { state: { user } }),
      external: false
    },
    {
      id: 'levito-iot',
      name: 'Levito IOT',
      description: 'Interactive Levito IOT demo — showcase the platform to customers',
      icon: Monitor,
      color: 'bg-orange-600',
      action: () => navigate('/levito-iot', { state: { user } }),
      external: false
    },
    {
      id: 'levito-fs',
      name: 'Levito Field Service',
      description: 'Interactive Levito Field Service Module demo — showcase the platform to customers',
      icon: Monitor,
      color: 'bg-pink-600',
      action: () => navigate('/levito-fs', { state: { user } }),
      external: false
    },
    {
      id: 'ref-docs',
      name: 'CME Reference Documents',
      description: 'Browse and download reference documents — PDFs, presentations, images and more',
      icon: BookOpen,
      color: 'bg-sky-600',
      action: () => navigate('/ref-docs', { state: { user } }),
      external: false
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#1a2332' }} data-testid="app-hub">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/cme-logo.png" alt="CME" className="h-8" />
            <span className="text-gray-400 text-sm font-normal">Sales Tools</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-400">
              {user?.picture && (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              )}
              <span className="text-sm">{user?.name || user?.email}</span>
            </div>
            <button
              onClick={handleLogout}
              className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1"
              data-testid="logout-btn"
            >
              <LogOut className="w-3 h-3" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* App Grid */}
      <main className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-semibold text-white mb-2">Your Tools</h2>
        <p className="text-gray-400 mb-10">Select a tool to get started</p>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map(app => (
            <button
              key={app.id}
              onClick={app.action}
              className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 
                rounded-xl p-6 text-left transition-all group"
              data-testid={`app-card-${app.id}`}
            >
              <div className={`w-12 h-12 ${app.color} rounded-lg flex items-center justify-center mb-4`}>
                <app.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                {app.name}
                {app.external && (
                  <span className="text-xs text-gray-500 ml-2 font-normal">External</span>
                )}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">{app.description}</p>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
};

// ==================== PROTECTED ROUTE ====================

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // 1. Check navigation state
    if (location.state?.user) {
      setUser(location.state.user);
      sessionStorage.setItem('cme_user', JSON.stringify(location.state.user));
      setChecking(false);
      return;
    }
    // 2. Check sessionStorage cache
    try {
      const cached = sessionStorage.getItem('cme_user');
      if (cached) {
        setUser(JSON.parse(cached));
        setChecking(false);
        return;
      }
    } catch {}
    // 3. Fall back to API check
    const checkAuth = async () => {
      try {
        const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (resp.data?.email) {
          setUser(resp.data);
          sessionStorage.setItem('cme_user', JSON.stringify(resp.data));
        } else {
          sessionStorage.removeItem('cme_user');
          navigate('/login', { replace: true });
        }
      } catch (err) {
        sessionStorage.removeItem('cme_user');
        navigate('/login', { replace: true });
      }
      setChecking(false);
    };
    checkAuth();
  }, [location.pathname, location.state?.user, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <Loader2 className="w-8 h-8 text-blue-500 spinner" />
      </div>
    );
  }

  if (!user) return null;

  return typeof children === 'function' ? children(user) : children;
};

// ==================== MAIN APP ====================

const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={
        <ProtectedRoute>
          {(user) => <AppHub user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/cpq" element={
        <ProtectedRoute>
          {(user) => <Configurator googleUser={user} />}
        </ProtectedRoute>
      } />
      <Route path="/heat-load" element={
        <ProtectedRoute>
          {(user) => <HeatLoadPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/spec-builder" element={
        <ProtectedRoute>
          {(user) => <TechSpecBuilder user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/envicom" element={
        <ProtectedRoute>
          {(user) => <EnvicomDemo user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/ramp-calculator" element={
        <ProtectedRoute>
          {(user) => <RampCalculator user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/presentation-builder" element={
        <ProtectedRoute>
          {(user) => <PresentationBuilder user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/envivom" element={
        <ProtectedRoute>
          {(user) => <EnvivomDemo user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/levito-iot" element={
        <ProtectedRoute>
          {(user) => <LevitoIotDemo user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/levito-fs" element={
        <ProtectedRoute>
          {(user) => <LevitoFsDemo user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/ref-docs" element={
        <ProtectedRoute>
          {(user) => <ReferenceDocuments user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin" element={<AdminPanel />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" toastOptions={{
        style: { background: '#121212', border: '1px solid rgba(255,255,255,0.1)', color: '#F3F4F6', fontFamily: '"JetBrains Mono", monospace', fontSize: '14px' }
      }} />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
